// Documentos de la ficha 360° (submenú reliv): Archivos Adjuntos (sobre
// medical.paciente_documento), Certificados (reusa el módulo) y seis documentos
// clínicos sobre medical.examen_registro (categorías del script 381):
// Informes, Consentimiento Informado, Orden de Ingreso, Interconsulta
// (solicitud+informe, candado 24h), Chequeo Preoperatorio y Protocolo
// Operatorio. Plantillas por documento en medical.plantilla (categoria propia).
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, Loader2, Paperclip, Pencil, Plus, Printer, Trash2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '@/lib/hooks';
import { AutoTextarea, Campo, Modal, compatibleConSexo, fieldCls, med, medRpc } from './shared';
import type { Diagnostico, Plantilla, Examen } from './shared';
import { DiagnosticosCie } from './DiagnosticosCie';
import { PlantillasDoc, RecetaEditor } from './RecetaEditor';
import type { ItemReliv } from './RecetaEditor';
import {
  ArchivosGuardados, BarraEx, CabeceraEx, CampoVer, Dropzone, Historial,
  editable24h, ds, fmtF, fmtFH, useEliminarExamen, useExamenes, useGuardarExamen,
} from './ExamenesFicha';
import type { ExamenRow, Staged, MisionEx } from './ExamenesFicha';
import { misionInline, VisorArchivo } from './ExamenesFicha';
import { NuevoCertificado, CertificadoPrint } from './Certificados';
import { RecetaPrint } from './Recetas';
import { normBuscar } from '@/features/inventario/shared';
import { calcIMC } from './FormularioClinico';
import { LOCALE } from '@/lib/utils/regional';

const esc = (v: unknown) => String(v ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));
const lbl = 'text-xs font-semibold uppercase text-muted-foreground';

function imprimirDoc({ titulo, sub, cuerpo, firmas = [] }: { titulo: string; sub: string; cuerpo: string; firmas?: string[] }) {
  const firmasHtml = firmas.length
    ? `<table class="firmas" style="margin-top:26px"><tbody><tr>${firmas.map((f) => `<td>${esc(f)}</td>`).join('')}</tr></tbody></table>` : '';
  const html = `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>${esc(titulo)}</title>
    <style>*{font-family:Arial,Helvetica,sans-serif}body{border:1.5px solid #333;padding:14px}
    h1{font-size:13px;margin:0 0 2px;text-align:center;text-transform:uppercase}.sub{font-size:11px;color:#555;margin:0 0 10px;text-align:center}
    h2{font-size:12px;margin:12px 0 4px;background:#eee;padding:3px 6px;border:1px solid #333}
    h3{font-size:11px;margin:8px 0 2px;text-transform:uppercase;color:#333}p{font-size:12px;margin:0;white-space:pre-wrap}
    table{border-collapse:collapse;width:100%;font-size:11px;margin-top:2px}td,th{border:1px solid #333;padding:3px 6px;vertical-align:top;white-space:pre-wrap}
    td.l{width:210px;font-weight:bold;background:#f7f7f7}th{background:#eee}.c{text-align:center}
    .firmas td{height:60px;vertical-align:bottom;text-align:center;font-size:10px}
    @media print{@page{margin:12mm}}</style></head><body>
    <h1>${esc(titulo)}</h1><p class="sub">${sub}</p>${cuerpo}${firmasHtml}
    <script>window.onload=function(){window.print();}</script></body></html>`;
  const win = window.open('', '_blank');
  if (!win) { toast.error('Permite las ventanas emergentes para imprimir'); return; }
  win.document.write(html); win.document.close();
}
const filaHtml = (l: string, v: string) => (v?.trim() ? `<tr><td class="l">${esc(l)}</td><td>${esc(v)}</td></tr>` : '');
const campoHtml = (l: string, v: string) => (v?.trim() ? `<h3>${esc(l)}</h3><p>${esc(v)}</p>` : '');
const dxHtml = (dx: Diagnostico[]) => (dx.length
  ? `<table><thead><tr><th>Código</th><th>CIE-10</th><th class="c">Presuntivo</th><th class="c">Definitivo</th></tr></thead><tbody>${dx.map((x) => `<tr><td>${esc(x.cie10_codigo ?? '—')}</td><td>${esc(x.descripcion)}</td><td class="c">${x.tipo === 'presuntivo' ? '●' : ''}</td><td class="c">${x.tipo === 'definitivo' ? '●' : ''}</td></tr>`).join('')}</tbody></table>` : '');

const dxDe = (r: ExamenRow): Diagnostico[] => (Array.isArray(r.datos.diagnosticos) ? (r.datos.diagnosticos as Diagnostico[]) : []);

function DxTabla({ dx }: { dx: Diagnostico[] }) {
  if (!dx.length) return null;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="border-b bg-muted text-xs text-muted-foreground">
          <th className="py-1.5 px-2 text-left">Código</th><th className="py-1.5 px-2 text-left">CIE-10</th>
          <th className="py-1.5 px-2 text-center">Presuntivo</th><th className="py-1.5 px-2 text-center">Definitivo</th>
        </tr></thead>
        <tbody>
          {dx.map((x, i) => (
            <tr key={i} className="border-b last:border-0">
              <td className="py-1.5 px-2 font-mono text-primary">{x.cie10_codigo ?? '—'}</td>
              <td className="py-1.5 px-2 uppercase">{x.descripcion}</td>
              <td className="py-1.5 px-2 text-center">{x.tipo === 'presuntivo' ? '●' : ''}</td>
              <td className="py-1.5 px-2 text-center">{x.tipo === 'definitivo' ? '●' : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1) Archivos Adjuntos — gestión de medical.paciente_documento
// ---------------------------------------------------------------------------
interface DocAdjunto { id: string; nombre: string; mime: string | null; archivo_url: string | null; fecha: string | null; observaciones: string | null; created_at: string }

export function ArchivosAdjuntos({ pacienteId, onVolver }: { pacienteId: string; onVolver: () => void }) {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [viendoDoc, setViendoDoc] = useState<string | null>(null);
  const { data: rows = [], isLoading } = useQuery<DocAdjunto[]>({
    queryKey: ['med-ficha', pacienteId, 'adjuntos'],
    queryFn: () => medRpc<DocAdjunto[]>('med_paciente_documentos', { _paciente_id: pacienteId }),
  });
  const [form, setForm] = useState<{ id?: string; descripcion: string; staged: Staged[] } | null>(null);
  const refrescar = () => void qc.invalidateQueries({ queryKey: ['med-ficha', pacienteId] });

  const guardar = useMutation({
    mutationFn: async () => {
      if (!form) throw new Error('Sin datos');
      if (form.id) {
        await med.update('paciente_documento', form.id, { observaciones: form.descripcion || null });
      } else {
        if (!form.staged.length) throw new Error('Sube al menos un archivo');
        for (const a of form.staged) {
          await med.create('paciente_documento', {
            paciente_id: pacienteId, nombre: a.nombre, mime: a.mime, archivo_url: a.archivo,
            tipo: 'otro', observaciones: form.descripcion || null,
          });
        }
      }
    },
    onSuccess: () => { toast.success('Guardado'); setForm(null); refrescar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => med.update('paciente_documento', id, { deleted_at: new Date().toISOString() }),
    onSuccess: () => { toast.success('Archivo eliminado'); refrescar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (isLoading) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>;
  return (
    <div className="space-y-3">
      {dlg.ui}
      <CabeceraEx titulo="Documentos — Archivos Adjuntos" onVolver={onVolver} />
      <section>
        <BarraEx titulo="Imágenes/PDF"
          accion={!form && <Button size="sm" onClick={() => setForm({ descripcion: '', staged: [] })}><Plus className="w-4 h-4 mr-1" /> Nueva Imagen/PDF</Button>} />
        <div className="rounded-b-md border border-t-0 bg-card p-4 space-y-3">
          {form && (
            <div className="space-y-3 rounded-md border p-3">
              {!form.id && <Dropzone staged={form.staged} setStaged={(f) => setForm((x) => (x ? { ...x, staged: f(x.staged) } : x))} />}
              <Campo label={form.id ? 'Editar Descripción:' : 'Descripción:'}>
                <AutoTextarea value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
              </Campo>
              <div className="flex gap-2">
                <Button size="sm" isLoading={guardar.isPending} onClick={() => guardar.mutate()}>Guardar</Button>
                <Button size="sm" variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
              </div>
            </div>
          )}
          {rows.length === 0 && !form ? (
            <p className="py-6 text-center text-sm text-primary/80">No existen imágenes/PDFs.</p>
          ) : rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b bg-muted text-xs text-muted-foreground">
                  <th className="py-2 px-2 text-left">Fecha</th><th className="py-2 px-2 text-left">Archivo</th>
                  <th className="py-2 px-2 text-left">Descripción</th><th className="py-2 px-2 text-right">Acción</th>
                </tr></thead>
                <tbody>
                  {rows.map((r) => (<Fragment key={r.id}>
                    <tr className="border-b last:border-0 hover:bg-muted/40">
                      <td className="py-2 px-2 whitespace-nowrap">{fmtFH(r.created_at)}</td>
                      <td className="py-2 px-2">
                        <span className="flex items-center gap-2">
                          {r.mime?.startsWith('image/') && r.archivo_url
                            ? <img src={r.archivo_url} alt={r.nombre} className="h-9 w-9 cursor-pointer rounded border object-cover" onClick={() => setViendoDoc(viendoDoc === r.id ? null : r.id)} />
                            : <Paperclip className="w-4 h-4 text-muted-foreground" />}
                          {r.archivo_url
                            ? <button type="button" className="inline-flex items-center gap-1 text-left text-primary hover:underline" onClick={() => setViendoDoc(viendoDoc === r.id ? null : r.id)}><Eye className="w-3.5 h-3.5 shrink-0" /> {r.nombre}</button>
                            : r.nombre}
                        </span>
                      </td>
                      <td className="py-2 px-2">{r.observaciones || '—'}</td>
                      <td className="py-2 px-2 text-right whitespace-nowrap">
                        <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar descripción" aria-label="Editar"
                          onClick={() => setForm({ id: r.id, descripcion: r.observaciones ?? '', staged: [] })}><Pencil className="w-4 h-4" /></button>
                        <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar" aria-label="Eliminar"
                          onClick={async () => { if (!(await dlg.confirmar(`¿Eliminar "${r.nombre}"?`, { peligro: true }))) return; eliminar.mutate(r.id); }}><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                    {viendoDoc === r.id && r.archivo_url && (
                      <tr className="border-b bg-muted/20"><td colSpan={4} className="px-2 py-2">
                        <VisorArchivo a={{ nombre: r.nombre, mime: r.mime ?? null, archivo: r.archivo_url }} onClose={() => setViendoDoc(null)} />
                      </td></tr>
                    )}
                  </Fragment>))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 2) Informes — texto libre con ciudad, plantillas e impresión
// ---------------------------------------------------------------------------
export function InformesFicha({ pacienteId, nombre, onVolver }: { pacienteId: string; nombre: string; onVolver: () => void }) {
  const { data: rows = [], isLoading } = useExamenes(pacienteId, 'informe');
  const guardar = useGuardarExamen(pacienteId, 'informe');
  const eliminar = useEliminarExamen(pacienteId);
  const [selId, setSelId] = useState<string | null>(null);
  const [form, setForm] = useState<{ id?: string; ciudad: string; informe: string } | null>(null);
  const dlg = useDialogoEnLinea();
  const sel = rows.find((r) => r.id === selId) ?? rows[0] ?? null;

  const imprimir = (r: ExamenRow) => imprimirDoc({
    titulo: 'Informe Médico',
    sub: `Paciente: <b>${esc(nombre)}</b> · ${esc(ds(r.datos, 'ciudad'))}, ${esc(fmtF(r.fecha))}`,
    cuerpo: `<p style="margin-top:8px">${esc(ds(r.datos, 'informe'))}</p>`,
    firmas: ['FIRMA Y SELLO DEL MÉDICO'],
  });

  if (isLoading) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>;
  return (
    <div className="space-y-3">
      {dlg.ui}
      <CabeceraEx titulo="Documentos — Informes" onVolver={onVolver} />
      <section>
        <BarraEx titulo="Informes"
          accion={!form && <Button size="sm" onClick={() => setForm({ ciudad: '', informe: '' })}><Plus className="w-4 h-4 mr-1" /> Nuevo Informe</Button>} />
        <div className="rounded-b-md border border-t-0 bg-card p-4">
          <div className="flex gap-4 items-start">
            <Historial rows={rows} selId={sel?.id ?? null} onSel={(id) => { setSelId(id); setForm(null); }} etiqueta={(r) => `Informe del ${fmtF(r.fecha)}`} />
            <div className="flex-1 min-w-0 space-y-3 text-sm">
              {form ? (<>
                <PlantillasDoc categoria="informe" getDatos={() => ({ ciudad: form.ciudad, informe: form.informe })}
                  onAplicar={(c) => setForm({ ...form, ciudad: (c.ciudad as string) ?? form.ciudad, informe: (c.informe as string) ?? '' })} />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Campo label="Ciudad:"><input className={fieldCls} placeholder="Ciudad" value={form.ciudad} onChange={(e) => setForm({ ...form, ciudad: e.target.value })} /></Campo>
                </div>
                <div>
                  <span className={lbl}>Diseña tu Informe:</span>
                  <AutoTextarea className="mt-1" value={form.informe} onChange={(e) => setForm({ ...form, informe: e.target.value })} />
                </div>
                <div className="flex gap-2 border-t pt-3">
                  <Button size="sm" isLoading={guardar.isPending} disabled={!form.informe.trim()}
                    onClick={() => guardar.mutate({ id: form.id, datos: { ciudad: form.ciudad, informe: form.informe }, staged: [] }, { onSuccess: () => setForm(null) })}>Guardar</Button>
                  <Button size="sm" variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
                </div>
              </>) : !sel ? (
                <p className="py-10 text-center text-sm text-primary/80">No existen informes realizados</p>
              ) : (<>
                <p className="font-semibold">Informe del {fmtF(sel.fecha)} <span className="font-normal text-muted-foreground">{ds(sel.datos, 'ciudad') && `· ${ds(sel.datos, 'ciudad')}`}</span></p>
                <CampoVer label="Informe:" v={ds(sel.datos, 'informe')} />
                <div className="flex gap-2 border-t pt-3">
                  <Button size="sm" variant="outline" onClick={() => imprimir(sel)}><Printer className="w-3.5 h-3.5 mr-1" /> Imprimir</Button>
                  <Button size="sm" variant="outline" onClick={() => setForm({ id: sel.id, ciudad: ds(sel.datos, 'ciudad'), informe: ds(sel.datos, 'informe') })}><Pencil className="w-3.5 h-3.5 mr-1" /> Editar</Button>
                  <Button size="sm" variant="outline" className="text-red-500" onClick={async () => { if (!(await dlg.confirmar('¿Eliminar este informe?', { peligro: true }))) return; eliminar.mutate(sel.id); }}><Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar</Button>
                  <Button size="sm" variant="outline" onClick={onVolver}>Regresar</Button>
                </div>
              </>)}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 3) Consentimiento Informado — 15 puntos numerados (reliv)
// ---------------------------------------------------------------------------
const CONSENT_CAMPOS: [string, string, 'input' | 'textarea'][] = [
  ['procedimiento', '1.- Nombre del Procedimiento:', 'input'],
  ['hospital', '2.- Hospital donde se va a realizar:', 'input'],
  ['servicio', '3.- Servicio hospitalario:', 'input'],
  ['diagnostico', '5.- Diagnóstico completo y código CIE-10:', 'textarea'],
  ['en_que_consiste', '6.- ¿En qué consiste el procedimiento?', 'textarea'],
  ['como_se_realiza', '7.- ¿Cómo se realiza el procedimiento?', 'textarea'],
  ['duracion', '8.- Duración estimada del procedimiento:', 'textarea'],
  ['beneficios', '9.- Beneficios del procedimiento:', 'textarea'],
  ['riesgos_frecuentes', '10.- Riesgos frecuentes (poco graves)', 'textarea'],
  ['riesgos_graves', '11.- Riesgos poco frecuentes (graves)', 'textarea'],
  ['riesgos_especificos', '12.- Riesgos específicos relacionados al paciente', 'textarea'],
  ['alternativas', '13.- Alternativas al procedimiento', 'textarea'],
  ['manejo_posterior', '14.- Descripción del manejo posterior al procedimiento', 'textarea'],
  ['consecuencias', '15.- Consecuencias posibles si no se realiza el procedimiento', 'textarea'],
];

export function ConsentimientoInformado({ pacienteId, nombre, identificacion, onVolver }: {
  pacienteId: string; nombre: string; identificacion?: string | null; onVolver: () => void;
}) {
  const { data: rows = [], isLoading } = useExamenes(pacienteId, 'consentimiento_informado');
  const guardar = useGuardarExamen(pacienteId, 'consentimiento_informado');
  const eliminar = useEliminarExamen(pacienteId);
  const [selId, setSelId] = useState<string | null>(null);
  const [form, setForm] = useState<{ id?: string; campos: Record<string, string>; tipo_atencion: string; staged: Staged[] } | null>(null);
  const dlg = useDialogoEnLinea();
  const sel = rows.find((r) => r.id === selId) ?? rows[0] ?? null;

  const abrir = (r?: ExamenRow) => setForm({
    id: r?.id,
    campos: Object.fromEntries(CONSENT_CAMPOS.map(([k]) => [k, r ? ds(r.datos, k) : ''])),
    tipo_atencion: r ? ds(r.datos, 'tipo_atencion') : '',
    staged: [],
  });

  const imprimir = (r: ExamenRow) => imprimirDoc({
    titulo: 'Consentimiento Informado',
    sub: `Paciente: <b>${esc(nombre)}</b>${identificacion ? ` · CI: ${esc(identificacion)}` : ''} · Fecha: ${esc(fmtF(r.fecha))}`,
    cuerpo: `<table><tbody>
        ${filaHtml('1. Nombre del procedimiento', ds(r.datos, 'procedimiento'))}
        ${filaHtml('2. Hospital donde se va a realizar', ds(r.datos, 'hospital'))}
        ${filaHtml('3. Servicio hospitalario', ds(r.datos, 'servicio'))}
        ${filaHtml('4. Tipo de atención', ds(r.datos, 'tipo_atencion'))}
      </tbody></table>
      ${CONSENT_CAMPOS.slice(3).map(([k, l]) => campoHtml(l.replace(/^\d+\.- /, (m) => m), ds(r.datos, k))).join('')}
      <p style="margin-top:10px;font-size:11px">Declaro que he sido informado/a de manera clara y comprensible sobre el procedimiento descrito,
      sus beneficios, riesgos y alternativas, he podido realizar preguntas y las mismas fueron respondidas satisfactoriamente,
      por lo que otorgo libremente mi consentimiento.</p>`,
    firmas: ['FIRMA DEL PACIENTE / REPRESENTANTE', 'FIRMA Y SELLO DEL MÉDICO'],
  });

  if (isLoading) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>;
  return (
    <div className="space-y-3">
      {dlg.ui}
      <CabeceraEx titulo="Documentos — Consentimiento Informado" onVolver={onVolver} />
      <section>
        <BarraEx titulo="Consentimientos Informados"
          accion={!form && <Button size="sm" onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nuevo Consentimiento</Button>} />
        <div className="rounded-b-md border border-t-0 bg-card p-4">
          <div className="flex gap-4 items-start">
            <Historial rows={rows} selId={sel?.id ?? null} onSel={(id) => { setSelId(id); setForm(null); }} etiqueta={(r) => `${ds(r.datos, 'procedimiento') || 'Consentimiento'} · ${fmtF(r.fecha)}`} />
            <div className="flex-1 min-w-0 space-y-3 text-sm">
              {form ? (<>
                <PlantillasDoc categoria="consentimiento_informado" getDatos={() => ({ ...form.campos, tipo_atencion: form.tipo_atencion })}
                  onAplicar={(c) => setForm({
                    ...form,
                    campos: Object.fromEntries(CONSENT_CAMPOS.map(([k]) => [k, typeof c[k] === 'string' ? (c[k] as string) : form.campos[k]])),
                    tipo_atencion: typeof c.tipo_atencion === 'string' ? (c.tipo_atencion as string) : form.tipo_atencion,
                  })} />
                {CONSENT_CAMPOS.slice(0, 3).map(([k, l]) => (
                  <Campo key={k} label={l}><input className={fieldCls} value={form.campos[k]} onChange={(e) => setForm({ ...form, campos: { ...form.campos, [k]: e.target.value } })} /></Campo>
                ))}
                <div>
                  <span className={lbl}>4.- Tipo de atención:</span>
                  <div className="mt-1 flex items-center gap-4">
                    {['Ambulatoria', 'Hospitalización'].map((t) => (
                      <label key={t} className="flex items-center gap-1.5 cursor-pointer">
                        <input type="radio" className="accent-primary" name="ci-tipo" checked={form.tipo_atencion === t} onChange={() => setForm({ ...form, tipo_atencion: t })} /> {t}
                      </label>
                    ))}
                  </div>
                </div>
                {CONSENT_CAMPOS.slice(3).map(([k, l]) => (
                  <div key={k}>
                    <span className={lbl}>{l}</span>
                    <AutoTextarea className="mt-1" value={form.campos[k]} onChange={(e) => setForm({ ...form, campos: { ...form.campos, [k]: e.target.value } })} />
                  </div>
                ))}
                {form.id && <ArchivosGuardados examenId={form.id} pacienteId={pacienteId} editable />}
                <Dropzone staged={form.staged} setStaged={(f) => setForm((x) => (x ? { ...x, staged: f(x.staged) } : x))} />
                <div className="flex gap-2 border-t pt-3">
                  <Button size="sm" isLoading={guardar.isPending} disabled={!form.campos.procedimiento.trim()}
                    onClick={() => guardar.mutate({ id: form.id, datos: { ...form.campos, tipo_atencion: form.tipo_atencion }, staged: form.staged }, { onSuccess: () => setForm(null) })}>Guardar</Button>
                  <Button size="sm" variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
                </div>
              </>) : !sel ? (
                <p className="py-10 text-center text-sm text-primary/80">No existen Consentimientos realizados</p>
              ) : (<>
                <p className="font-semibold">Consentimiento Informado del {fmtF(sel.fecha)}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 rounded-md border px-3 py-2 text-xs">
                  <p><b>Procedimiento:</b> {ds(sel.datos, 'procedimiento') || '—'}</p>
                  <p><b>Hospital:</b> {ds(sel.datos, 'hospital') || '—'}</p>
                  <p><b>Servicio:</b> {ds(sel.datos, 'servicio') || '—'}</p>
                  <p><b>Tipo de atención:</b> {ds(sel.datos, 'tipo_atencion') || '—'}</p>
                </div>
                {CONSENT_CAMPOS.slice(3).map(([k, l]) => <CampoVer key={k} label={l} v={ds(sel.datos, k)} />)}
                <ArchivosGuardados examenId={sel.id} pacienteId={pacienteId} editable={false} />
                <div className="flex gap-2 border-t pt-3">
                  <Button size="sm" variant="outline" onClick={() => imprimir(sel)}><Printer className="w-3.5 h-3.5 mr-1" /> Imprimir</Button>
                  <Button size="sm" variant="outline" onClick={() => abrir(sel)}><Pencil className="w-3.5 h-3.5 mr-1" /> Editar</Button>
                  <Button size="sm" variant="outline" className="text-red-500" onClick={async () => { if (!(await dlg.confirmar('¿Eliminar este consentimiento?', { peligro: true }))) return; eliminar.mutate(sel.id); }}><Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar</Button>
                  <Button size="sm" variant="outline" onClick={onVolver}>Regresar</Button>
                </div>
              </>)}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 4) Orden de Ingreso
// ---------------------------------------------------------------------------
interface FormOrden {
  id?: string; fecha_ingreso: string; hora_ingreso: string; hospital: string; servicio: string; servicio_otros: string;
  especialidad: string; dx: Diagnostico[]; con_procedimiento: boolean; procedimiento: string; fecha_procedimiento: string;
  hora_procedimiento: string; requerimientos: string; instrumental: string; indicaciones: string[]; indicacion_nueva: string;
  observaciones: string;
}

export function OrdenIngreso({ pacienteId, nombre, mision, onVolver }: { pacienteId: string; nombre: string; mision?: MisionEx; onVolver: () => void }) {
  const { data: rows = [], isLoading } = useExamenes(pacienteId, 'orden_ingreso');
  const guardar = useGuardarExamen(pacienteId, 'orden_ingreso');
  const eliminar = useEliminarExamen(pacienteId);
  const [selId, setSelId] = useState<string | null>(null);
  const [form, setForm] = useState<FormOrden | null>(null);
  const dlg = useDialogoEnLinea();
  const sel = rows.find((r) => r.id === selId) ?? rows[0] ?? null;

  const abrir = (r?: ExamenRow) => setForm({
    id: r?.id, fecha_ingreso: r ? ds(r.datos, 'fecha_ingreso') : '', hora_ingreso: r ? ds(r.datos, 'hora_ingreso') : '',
    hospital: r ? ds(r.datos, 'hospital') : '', servicio: r ? ds(r.datos, 'servicio') : 'Hospitalización',
    servicio_otros: r ? ds(r.datos, 'servicio_otros') : '', especialidad: r ? ds(r.datos, 'especialidad') : '',
    dx: r ? dxDe(r) : [], con_procedimiento: r ? r.datos.con_procedimiento === true : false,
    procedimiento: r ? ds(r.datos, 'procedimiento') : '', fecha_procedimiento: r ? ds(r.datos, 'fecha_procedimiento') : '',
    hora_procedimiento: r ? ds(r.datos, 'hora_procedimiento') : '', requerimientos: r ? ds(r.datos, 'requerimientos') : '',
    instrumental: r ? ds(r.datos, 'instrumental') : '',
    indicaciones: r && Array.isArray(r.datos.indicaciones) ? (r.datos.indicaciones as string[]) : [],
    indicacion_nueva: '', observaciones: r ? ds(r.datos, 'observaciones') : '',
  });

  const datosDe = (f: FormOrden): Record<string, unknown> => {
    const { id: _id, dx, indicacion_nueva: _in, ...resto } = f;
    return { ...resto, diagnosticos: dx };
  };

  const imprimir = (r: ExamenRow) => imprimirDoc({
    titulo: 'Orden de Ingreso',
    sub: `Paciente: <b>${esc(nombre)}</b>${misionInline(mision ?? null)} · Orden N° ${esc(r.numero ?? '—')} · ${esc(fmtF(r.fecha))}`,
    cuerpo: `<table><tbody>
        ${filaHtml('Fecha de ingreso', `${fmtF(ds(r.datos, 'fecha_ingreso'))} ${ds(r.datos, 'hora_ingreso')}`)}
        ${filaHtml('Hospital/Clínica', ds(r.datos, 'hospital'))}
        ${filaHtml('Servicio', `${ds(r.datos, 'servicio')}${ds(r.datos, 'servicio_otros') ? ` — ${ds(r.datos, 'servicio_otros')}` : ''}`)}
        ${filaHtml('Especialidad', ds(r.datos, 'especialidad'))}
      </tbody></table>
      ${campoHtml('Diagnóstico', '')}${dxHtml(dxDe(r))}
      ${r.datos.con_procedimiento === true ? `<h2>PROCEDIMIENTO</h2><table><tbody>
        ${filaHtml('Procedimiento', ds(r.datos, 'procedimiento'))}
        ${filaHtml('Fecha / hora', `${fmtF(ds(r.datos, 'fecha_procedimiento'))} ${ds(r.datos, 'hora_procedimiento')}`)}
        ${filaHtml('Requerimientos especiales', ds(r.datos, 'requerimientos'))}
        ${filaHtml('Instrumental', ds(r.datos, 'instrumental'))}
      </tbody></table>` : ''}
      ${Array.isArray(r.datos.indicaciones) && (r.datos.indicaciones as string[]).length
        ? `<h2>INDICACIONES</h2><table><tbody>${(r.datos.indicaciones as string[]).map((x, i) => `<tr><td class="l">${i + 1}</td><td>${esc(x)}</td></tr>`).join('')}</tbody></table>` : ''}
      ${campoHtml('Observaciones', ds(r.datos, 'observaciones'))}`,
    firmas: ['FIRMA Y SELLO DEL MÉDICO'],
  });

  if (isLoading) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>;
  return (
    <div className="space-y-3">
      {dlg.ui}
      <CabeceraEx titulo="Documentos — Orden de Ingreso" onVolver={onVolver} />
      <section>
        <BarraEx titulo="Orden de Ingreso"
          accion={!form && <Button size="sm" onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nuevo Ingreso</Button>} />
        <div className="rounded-b-md border border-t-0 bg-card p-4">
          <div className="flex gap-4 items-start">
            <Historial rows={rows} selId={sel?.id ?? null} onSel={(id) => { setSelId(id); setForm(null); }} etiqueta={(r) => `Orden ${r.numero ?? ''} · ${fmtF(r.fecha)}`} />
            <div className="flex-1 min-w-0 space-y-3 text-sm">
              {form ? (<>
                <PlantillasDoc categoria="orden_ingreso" getDatos={() => datosDe(form)}
                  onAplicar={(c) => setForm({
                    ...form,
                    hospital: (c.hospital as string) ?? form.hospital, servicio: (c.servicio as string) ?? form.servicio,
                    especialidad: (c.especialidad as string) ?? form.especialidad,
                    procedimiento: (c.procedimiento as string) ?? form.procedimiento,
                    requerimientos: (c.requerimientos as string) ?? form.requerimientos,
                    instrumental: (c.instrumental as string) ?? form.instrumental,
                    indicaciones: Array.isArray(c.indicaciones) ? (c.indicaciones as string[]) : form.indicaciones,
                    observaciones: (c.observaciones as string) ?? form.observaciones,
                  })} />
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Campo label="Fecha de ingreso"><input type="date" className={fieldCls} value={form.fecha_ingreso} onChange={(e) => setForm({ ...form, fecha_ingreso: e.target.value })} /></Campo>
                  <Campo label="Hora de ingreso"><input type="time" className={fieldCls} value={form.hora_ingreso} onChange={(e) => setForm({ ...form, hora_ingreso: e.target.value })} /></Campo>
                  <Campo label="Hospital/Clínica" className="col-span-2"><input className={fieldCls} value={form.hospital} onChange={(e) => setForm({ ...form, hospital: e.target.value })} /></Campo>
                </div>
                <div>
                  <span className={lbl}>Servicio:</span>
                  <div className="mt-1 flex flex-wrap items-center gap-4">
                    {['Hospitalización', 'Cirugia', 'Emergencia', 'Otros'].map((t) => (
                      <label key={t} className="flex items-center gap-1.5 cursor-pointer">
                        <input type="radio" className="accent-primary" name="oi-serv" checked={form.servicio === t} onChange={() => setForm({ ...form, servicio: t })} /> {t === 'Cirugia' ? 'Cirugía' : t}
                      </label>
                    ))}
                    {form.servicio === 'Otros' && <input className={`${fieldCls} w-56`} placeholder="Especifica…" value={form.servicio_otros} onChange={(e) => setForm({ ...form, servicio_otros: e.target.value })} />}
                  </div>
                </div>
                <Campo label="Especialidad"><input className={fieldCls} value={form.especialidad} onChange={(e) => setForm({ ...form, especialidad: e.target.value })} /></Campo>
                <span className={lbl}>Diagnóstico</span>
                <DiagnosticosCie dx={form.dx} setDx={(dx) => setForm((f) => (f ? { ...f, dx } : f))} />
                <label className="flex items-center gap-2 cursor-pointer text-sm font-medium">
                  <input type="checkbox" className="accent-primary" checked={form.con_procedimiento} onChange={(e) => setForm({ ...form, con_procedimiento: e.target.checked })} /> PROCEDIMIENTO
                </label>
                {form.con_procedimiento && (
                  <div className="space-y-3 rounded-md border p-3">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <Campo label="Procedimiento" className="col-span-2"><AutoTextarea value={form.procedimiento} onChange={(e) => setForm({ ...form, procedimiento: e.target.value })} /></Campo>
                      <Campo label="Fecha de procedimiento"><input type="date" className={fieldCls} value={form.fecha_procedimiento} onChange={(e) => setForm({ ...form, fecha_procedimiento: e.target.value })} /></Campo>
                      <Campo label="Hora de procedimiento"><input type="time" className={fieldCls} value={form.hora_procedimiento} onChange={(e) => setForm({ ...form, hora_procedimiento: e.target.value })} /></Campo>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div><span className={lbl}>Requerimientos Especiales:</span><AutoTextarea className="mt-1" value={form.requerimientos} onChange={(e) => setForm({ ...form, requerimientos: e.target.value })} /></div>
                      <div><span className={lbl}>Instrumental:</span><AutoTextarea className="mt-1" value={form.instrumental} onChange={(e) => setForm({ ...form, instrumental: e.target.value })} /></div>
                    </div>
                  </div>
                )}
                <div>
                  <span className={lbl}>Indicaciones:</span>
                  {form.indicaciones.length > 0 && (
                    <ul className="mt-1 divide-y rounded-md border">
                      {form.indicaciones.map((x, i) => (
                        <li key={i} className="flex items-center justify-between px-3 py-1.5">
                          <span className="whitespace-pre-wrap">{i + 1}. {x}</span>
                          <button type="button" className="text-red-500" title="Quitar" aria-label="Quitar"
                            onClick={() => setForm({ ...form, indicaciones: form.indicaciones.filter((_, j) => j !== i) })}><Trash2 className="w-3.5 h-3.5" /></button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-1 flex gap-2">
                    <AutoTextarea className="flex-1" placeholder="Nueva indicación…" value={form.indicacion_nueva} onChange={(e) => setForm({ ...form, indicacion_nueva: e.target.value })} />
                    <Button size="sm" variant="outline" disabled={!form.indicacion_nueva.trim()}
                      onClick={() => setForm({ ...form, indicaciones: [...form.indicaciones, form.indicacion_nueva.trim()], indicacion_nueva: '' })}>Agregar</Button>
                  </div>
                </div>
                <div><span className={lbl}>Observaciones:</span><AutoTextarea className="mt-1" value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} /></div>
                <div className="flex gap-2 border-t pt-3">
                  <Button size="sm" isLoading={guardar.isPending} onClick={() => guardar.mutate({ id: form.id, datos: datosDe(form), staged: [] }, { onSuccess: () => setForm(null) })}>Guardar</Button>
                  <Button size="sm" variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
                </div>
              </>) : !sel ? (
                <p className="py-10 text-center text-sm text-primary/80">No existen Órdenes de Ingreso realizadas</p>
              ) : (<>
                <p className="font-semibold">Orden de Ingreso N° {sel.numero ?? '—'} <span className="font-normal text-muted-foreground">· {fmtF(sel.fecha)}</span></p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 rounded-md border px-3 py-2 text-xs">
                  <p><b>Ingreso:</b> {fmtF(ds(sel.datos, 'fecha_ingreso'))} {ds(sel.datos, 'hora_ingreso')}</p>
                  <p><b>Hospital:</b> {ds(sel.datos, 'hospital') || '—'}</p>
                  <p><b>Servicio:</b> {ds(sel.datos, 'servicio')}{ds(sel.datos, 'servicio_otros') && ` (${ds(sel.datos, 'servicio_otros')})`}</p>
                  <p><b>Especialidad:</b> {ds(sel.datos, 'especialidad') || '—'}</p>
                </div>
                <DxTabla dx={dxDe(sel)} />
                {sel.datos.con_procedimiento === true && (<>
                  <CampoVer label="Procedimiento" v={`${ds(sel.datos, 'procedimiento')}${ds(sel.datos, 'fecha_procedimiento') ? `\nFecha: ${fmtF(ds(sel.datos, 'fecha_procedimiento'))} ${ds(sel.datos, 'hora_procedimiento')}` : ''}`} />
                  <CampoVer label="Requerimientos Especiales" v={ds(sel.datos, 'requerimientos')} />
                  <CampoVer label="Instrumental" v={ds(sel.datos, 'instrumental')} />
                </>)}
                {Array.isArray(sel.datos.indicaciones) && (sel.datos.indicaciones as string[]).length > 0 && (
                  <div>
                    <h4 className={lbl}>Indicaciones</h4>
                    <ul className="mt-1 list-decimal space-y-0.5 pl-5">
                      {(sel.datos.indicaciones as string[]).map((x, i) => <li key={i} className="whitespace-pre-wrap">{x}</li>)}
                    </ul>
                  </div>
                )}
                <CampoVer label="Observaciones" v={ds(sel.datos, 'observaciones')} />
                <div className="flex gap-2 border-t pt-3">
                  <Button size="sm" variant="outline" onClick={() => imprimir(sel)}><Printer className="w-3.5 h-3.5 mr-1" /> Imprimir</Button>
                  <Button size="sm" variant="outline" onClick={() => abrir(sel)}><Pencil className="w-3.5 h-3.5 mr-1" /> Editar</Button>
                  <Button size="sm" variant="outline" className="text-red-500" onClick={async () => { if (!(await dlg.confirmar('¿Eliminar esta orden?', { peligro: true }))) return; eliminar.mutate(sel.id); }}><Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar</Button>
                  <Button size="sm" variant="outline" onClick={onVolver}>Regresar</Button>
                </div>
              </>)}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 5) Certificados — reusa el módulo (lista + crear + imprimir)
// ---------------------------------------------------------------------------
interface CertRow { id: string; codigo: string | null; fecha: string; tipo: string | null; estado: string; diagnostico: string | null; dias_reposo: number | null; medico: string }

export function CertificadosFicha({ pacienteId, onVolver }: { pacienteId: string; onVolver: () => void }) {
  const qc = useQueryClient();
  const { data: certs = [], isLoading } = useQuery<CertRow[]>({
    queryKey: ['med-ficha', pacienteId, 'certificados-doc'],
    queryFn: () => medRpc<CertRow[]>('med_certificados', { _paciente_id: pacienteId }),
  });
  const [nuevo, setNuevo] = useState(false);
  const [imprimir, setImprimir] = useState<string | null>(null);

  if (isLoading) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>;
  return (
    <div className="space-y-3">
      <CabeceraEx titulo="Documentos — Certificados" onVolver={onVolver} />
      <section>
        <BarraEx titulo="Certificados"
          accion={<Button size="sm" onClick={() => setNuevo(true)}><Plus className="w-4 h-4 mr-1" /> Nuevo Certificado</Button>} />
        <div className="rounded-b-md border border-t-0 bg-card p-4 overflow-x-auto">
          {certs.length === 0 ? (
            <p className="py-6 text-center text-sm text-primary/80">No existen certificados</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="border-b bg-muted text-xs text-muted-foreground">
                <th className="py-2 px-2 text-left">Código</th><th className="py-2 px-2 text-left">Fecha</th>
                <th className="py-2 px-2 text-left">Tipo</th><th className="py-2 px-2 text-left">Diagnóstico</th>
                <th className="py-2 px-2 text-left">Reposo</th><th className="py-2 px-2 text-left">Médico</th>
                <th className="py-2 px-2 text-left">Estado</th><th className="py-2 px-2 text-right">Acción</th>
              </tr></thead>
              <tbody>
                {certs.map((c) => (
                  <tr key={c.id} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="py-2 px-2 font-mono">{c.codigo}</td>
                    <td className="py-2 px-2 whitespace-nowrap">{fmtF(c.fecha)}</td>
                    <td className="py-2 px-2 capitalize">{c.tipo ?? '—'}</td>
                    <td className="py-2 px-2">{c.diagnostico || '—'}</td>
                    <td className="py-2 px-2">{c.dias_reposo ? `${c.dias_reposo} día(s)` : '—'}</td>
                    <td className="py-2 px-2">{c.medico}</td>
                    <td className="py-2 px-2 capitalize">{c.estado}</td>
                    <td className="py-2 px-2 text-right">
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Imprimir" aria-label="Imprimir" onClick={() => setImprimir(c.id)}><Printer className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
      {nuevo && <NuevoCertificado pacienteId={pacienteId} onClose={() => setNuevo(false)}
        onSaved={() => { setNuevo(false); void qc.invalidateQueries({ queryKey: ['med-ficha', pacienteId] }); }} />}
      {imprimir && <CertificadoPrint id={imprimir} onClose={() => setImprimir(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 6) Interconsulta — Solicitud + Informe (candado 24h)
// ---------------------------------------------------------------------------
const INTER_SOL: [string, string][] = [
  ['motivo', 'MOTIVO DE INTERCONSULTA:'], ['servicio_interconsulta', 'SERVICIO INTERCONSULTA'],
  ['medico_interconsultado', 'MÉDICO INTERCONSULTADO'], ['cuadro_clinico', 'CUADRO CLÍNICO'],
  ['examenes_diagnosticos', 'EXÁMENES Y PROCEDIMIENTOS DIAGNÓSTICOS'], ['analisis', 'ANÁLISIS - DIAGNÓSTICO'],
  ['tratamiento', 'TRATAMIENTO'], ['servicio_consultado', 'SERVICIO CONSULTADO'],
];
const INTER_INF: [string, string][] = [
  ['inf_motivo', 'MOTIVO DE INTERCONSULTA:'], ['inf_medico', 'MÉDICO QUE INTERCONSULTA'],
  ['inf_cuadro_clinico', 'CUADRO CLÍNICO DE INTERCONSULTA'], ['inf_resumen', 'RESUMEN DEL CRITERIO CLÍNICO'],
  ['inf_diagnosticos', 'DIAGNÓSTICOS'], ['inf_examenes', 'EXÁMENES COMPLEMENTARIOS'],
  ['inf_plan', 'PLAN TRATAMIENTO PROPUESTO'],
];

export function InterconsultaFicha({ pacienteId, nombre, mision, onVolver }: { pacienteId: string; nombre: string; mision?: MisionEx; onVolver: () => void }) {
  const { data: rows = [], isLoading } = useExamenes(pacienteId, 'interconsulta');
  const guardar = useGuardarExamen(pacienteId, 'interconsulta');
  const eliminar = useEliminarExamen(pacienteId);
  const [selId, setSelId] = useState<string | null>(null);
  const [tab, setTab] = useState<'solicitud' | 'informe'>('solicitud');
  const [form, setForm] = useState<{ id?: string; campos: Record<string, string>; prioridad: string; dxSol: Diagnostico[]; dxInf: Diagnostico[]; staged: Staged[] } | null>(null);
  const [printMenu, setPrintMenu] = useState(false);
  const dlg = useDialogoEnLinea();
  const sel = rows.find((r) => r.id === selId) ?? rows[0] ?? null;

  const abrir = (r?: ExamenRow) => setForm({
    id: r?.id,
    campos: Object.fromEntries([...INTER_SOL, ...INTER_INF].map(([k]) => [k, r ? ds(r.datos, k) : ''])),
    prioridad: r ? (ds(r.datos, 'prioridad') || 'Normal') : 'Normal',
    dxSol: r ? dxDe(r) : [],
    dxInf: r && Array.isArray(r.datos.diagnosticos_informe) ? (r.datos.diagnosticos_informe as Diagnostico[]) : [],
    staged: [],
  });

  const imprimir = (r: ExamenRow, parte: 'solicitud' | 'informe') => imprimirDoc({
    titulo: `Interconsulta — ${parte === 'solicitud' ? 'Solicitud' : 'Informe'}`,
    sub: `Paciente: <b>${esc(nombre)}</b>${misionInline(mision ?? null)} · Interconsulta N° ${esc(r.numero ?? '—')} · ${esc(fmtF(r.fecha))} · Prioridad: ${esc(ds(r.datos, 'prioridad') || 'Normal')}`,
    cuerpo: parte === 'solicitud'
      ? `${INTER_SOL.map(([k, l]) => campoHtml(l, ds(r.datos, k))).join('')}${dxHtml(dxDe(r))}`
      : `${INTER_INF.map(([k, l]) => campoHtml(l, ds(r.datos, k))).join('')}${dxHtml(Array.isArray(r.datos.diagnosticos_informe) ? (r.datos.diagnosticos_informe as Diagnostico[]) : [])}`,
    firmas: parte === 'solicitud' ? ['FIRMA DEL MÉDICO SOLICITANTE'] : ['FIRMA DEL MÉDICO INTERCONSULTADO'],
  });

  if (isLoading) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>;
  const camposTab = tab === 'solicitud' ? INTER_SOL : INTER_INF;
  return (
    <div className="space-y-3">
      {dlg.ui}
      <CabeceraEx titulo="Documentos — Interconsulta" onVolver={onVolver} />
      <section>
        <BarraEx titulo="Interconsultas"
          accion={!form && <Button size="sm" onClick={() => { abrir(); setTab('solicitud'); }}><Plus className="w-4 h-4 mr-1" /> Nueva interconsulta</Button>} />
        <div className="rounded-b-md border border-t-0 bg-card p-4 space-y-3">
          <p className="rounded-md bg-primary/10 px-3 py-2 text-xs">
            ℹ️ Estimado Dr/a: le recordamos que las interconsultas se podrán modificar únicamente hasta 24 horas posterior a su creación.
          </p>
          <div className="flex gap-4 items-start">
            <Historial rows={rows} selId={sel?.id ?? null} onSel={(id) => { setSelId(id); setForm(null); }} etiqueta={(r) => `Interconsulta ${r.numero ?? ''} · ${fmtF(r.fecha)}`} />
            <div className="flex-1 min-w-0 space-y-3 text-sm">
              {(form || sel) && (
                <div className="flex gap-1 border-b">
                  {(['solicitud', 'informe'] as const).map((t) => (
                    <button key={t} type="button" onClick={() => setTab(t)}
                      className={`px-3 py-2 text-sm -mb-px border-b-2 uppercase ${tab === t ? 'border-primary text-primary font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
                      {t}
                    </button>
                  ))}
                </div>
              )}
              {form ? (<>
                {camposTab.map(([k, l]) => (
                  <div key={k}>
                    <span className={lbl}>{l}</span>
                    <AutoTextarea className="mt-1" value={form.campos[k]} onChange={(e) => setForm({ ...form, campos: { ...form.campos, [k]: e.target.value } })} />
                  </div>
                ))}
                {tab === 'solicitud' ? (<>
                  <DiagnosticosCie dx={form.dxSol} setDx={(dx) => setForm((f) => (f ? { ...f, dxSol: dx } : f))} />
                  <div className="flex items-center gap-4">
                    {['Normal', 'Urgente'].map((t) => (
                      <label key={t} className="flex items-center gap-1.5 cursor-pointer">
                        <input type="radio" className="accent-primary" name="ic-prio" checked={form.prioridad === t} onChange={() => setForm({ ...form, prioridad: t })} /> {t}
                      </label>
                    ))}
                  </div>
                </>) : (
                  <DiagnosticosCie dx={form.dxInf} setDx={(dx) => setForm((f) => (f ? { ...f, dxInf: dx } : f))} />
                )}
                {form.id && <ArchivosGuardados examenId={form.id} pacienteId={pacienteId} editable />}
                <Dropzone staged={form.staged} setStaged={(f) => setForm((x) => (x ? { ...x, staged: f(x.staged) } : x))} />
                <div className="flex gap-2 border-t pt-3">
                  <Button size="sm" isLoading={guardar.isPending}
                    onClick={() => guardar.mutate({
                      id: form.id,
                      datos: { ...form.campos, prioridad: form.prioridad, diagnosticos: form.dxSol, diagnosticos_informe: form.dxInf },
                      staged: form.staged,
                    }, { onSuccess: () => setForm(null) })}>Guardar</Button>
                  <Button size="sm" variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
                </div>
              </>) : !sel ? (
                <p className="py-10 text-center text-sm text-primary/80">No existe historial de Interconsultas</p>
              ) : (<>
                <p className="font-semibold">Interconsulta N° {sel.numero ?? '—'} <span className="font-normal text-muted-foreground">· {fmtFH(sel.fecha)} · Prioridad: {ds(sel.datos, 'prioridad') || 'Normal'}</span></p>
                {camposTab.map(([k, l]) => <CampoVer key={k} label={l} v={ds(sel.datos, k)} />)}
                <DxTabla dx={tab === 'solicitud' ? dxDe(sel) : (Array.isArray(sel.datos.diagnosticos_informe) ? (sel.datos.diagnosticos_informe as Diagnostico[]) : [])} />
                <ArchivosGuardados examenId={sel.id} pacienteId={pacienteId} editable={false} />
                <div className="flex gap-2 border-t pt-3">
                  <div className="relative">
                    <Button size="sm" variant="outline" onClick={() => setPrintMenu((o) => !o)}><Printer className="w-3.5 h-3.5 mr-1" /> Imprimir <ChevronDown className="w-3.5 h-3.5 ml-1" /></Button>
                    {printMenu && (
                      <div className="absolute left-0 z-10 mt-1 w-32 rounded-md border bg-card shadow-lg">
                        <button type="button" className="block w-full px-3 py-1.5 text-left text-sm hover:bg-muted" onClick={() => { setPrintMenu(false); imprimir(sel, 'solicitud'); }}>Solicitud</button>
                        <button type="button" className="block w-full px-3 py-1.5 text-left text-sm hover:bg-muted" onClick={() => { setPrintMenu(false); imprimir(sel, 'informe'); }}>Informe</button>
                      </div>
                    )}
                  </div>
                  {editable24h(sel) && <Button size="sm" variant="outline" onClick={() => abrir(sel)}><Pencil className="w-3.5 h-3.5 mr-1" /> Editar</Button>}
                  {editable24h(sel) && <Button size="sm" variant="outline" className="text-red-500" onClick={async () => { if (!(await dlg.confirmar('¿Eliminar esta interconsulta?', { peligro: true }))) return; eliminar.mutate(sel.id); }}><Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar</Button>}
                  <Button size="sm" variant="outline" onClick={onVolver}>Regresar</Button>
                </div>
              </>)}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 7) Chequeo Preoperatorio
// ---------------------------------------------------------------------------
const CHEQUEO_CAMPOS: [string, string][] = [
  ['motivo', 'MOTIVO DE CONSULTA:'], ['medico_solicita', 'MÉDICO QUE SOLICITA'],
  ['enfermedad_actual', 'ENFERMEDAD ACTUAL'], ['examen_fisico', 'EXAMEN FÍSICO'],
  ['examenes_complementarios', 'EXÁMENES COMPLEMENTARIOS'], ['diagnostico', 'DIAGNÓSTICO'],
];
const CHEQUEO_SECCIONES: [string, string, [string, string][]][] = [
  ['ete', 'Enfermedad Tromboembólica', [['incidencia_tvp', 'INCIDENCIA DE TVP:'], ['nivel_riesgo', 'NIVEL DE RIESGO:'], ['regimen_profilaxis', 'RÉGIMEN DE PROFILAXIS:']]],
  ['asa', 'ASA', [['asa_clasificacion', 'CLASIFICACIÓN:'], ['asa_mortalidad', 'MORTALIDAD PERIOPERATORIA:']]],
  ['aha', 'AHA', [['aha_clasificacion', 'CLASIFICACIÓN:'], ['aha_clase', 'CLASE:']]],
];
const SIGNOS_CHEQUEO: [string, string][] = [
  ['fc', 'FC'], ['fr', 'FR'], ['so2', 'SO2'], ['ta', 'TA'], ['temperatura', 'Temperatura'], ['peso', 'Peso (kg)'], ['talla', 'Talla (cm)'],
];

export function ChequeoPreoperatorio({ pacienteId, nombre, mision, onVolver }: { pacienteId: string; nombre: string; mision?: MisionEx; onVolver: () => void }) {
  const { data: rows = [], isLoading } = useExamenes(pacienteId, 'chequeo_preop');
  const guardar = useGuardarExamen(pacienteId, 'chequeo_preop');
  const eliminar = useEliminarExamen(pacienteId);
  const [selId, setSelId] = useState<string | null>(null);
  const [form, setForm] = useState<{ id?: string; campos: Record<string, string>; dx: Diagnostico[]; staged: Staged[] } | null>(null);
  const dlg = useDialogoEnLinea();
  const sel = rows.find((r) => r.id === selId) ?? rows[0] ?? null;

  const TODOS = [...SIGNOS_CHEQUEO, ...CHEQUEO_CAMPOS, ...CHEQUEO_SECCIONES.flatMap(([, , c]) => c), ['recomendaciones', 'RECOMENDACIONES'] as [string, string]];
  const abrir = (r?: ExamenRow) => setForm({
    id: r?.id,
    campos: Object.fromEntries(TODOS.map(([k]) => [k, r ? ds(r.datos, k) : ''])),
    dx: r ? dxDe(r) : [], staged: [],
  });

  const imcDe = (c: Record<string, string>) => calcIMC(c.peso, c.talla) ?? '';

  const imprimir = (r: ExamenRow) => imprimirDoc({
    titulo: 'Chequeo Preoperatorio — Informe',
    sub: `Paciente: <b>${esc(nombre)}</b>${misionInline(mision ?? null)} · Chequeo N° ${esc(r.numero ?? '—')} · ${esc(fmtF(r.fecha))}`,
    cuerpo: `<p class="dato" style="font-size:11px"><b>FC:</b> ${esc(ds(r.datos, 'fc') || '—')} · <b>FR:</b> ${esc(ds(r.datos, 'fr') || '—')} · <b>SO2:</b> ${esc(ds(r.datos, 'so2') || '—')} · <b>TA:</b> ${esc(ds(r.datos, 'ta') || '—')} · <b>T°:</b> ${esc(ds(r.datos, 'temperatura') || '—')} · <b>Peso:</b> ${esc(ds(r.datos, 'peso') || '—')} kg · <b>Talla:</b> ${esc(ds(r.datos, 'talla') || '—')} · <b>IMC:</b> ${esc(calcIMC(ds(r.datos, 'peso'), ds(r.datos, 'talla')) ?? '—')}</p>
      ${CHEQUEO_CAMPOS.map(([k, l]) => campoHtml(l, ds(r.datos, k))).join('')}${dxHtml(dxDe(r))}
      ${CHEQUEO_SECCIONES.map(([, tit, campos]) => {
        const filas = campos.map(([k, l]) => filaHtml(l, ds(r.datos, k))).join('');
        return filas ? `<h2>${esc(tit)}</h2><table><tbody>${filas}</tbody></table>` : '';
      }).join('')}
      ${campoHtml('RECOMENDACIONES', ds(r.datos, 'recomendaciones'))}`,
    firmas: ['FIRMA Y SELLO DEL MÉDICO'],
  });

  if (isLoading) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>;
  return (
    <div className="space-y-3">
      {dlg.ui}
      <CabeceraEx titulo="Documentos — Chequeo Preoperatorio" onVolver={onVolver} />
      <section>
        <BarraEx titulo="Chequeos Preoperatorios"
          accion={!form && <Button size="sm" onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nuevo Chequeo</Button>} />
        <div className="rounded-b-md border border-t-0 bg-card p-4">
          <div className="flex gap-4 items-start">
            <Historial rows={rows} selId={sel?.id ?? null} onSel={(id) => { setSelId(id); setForm(null); }} etiqueta={(r) => `Chequeo ${r.numero ?? ''} · ${fmtF(r.fecha)}`} />
            <div className="flex-1 min-w-0 space-y-3 text-sm">
              {form ? (<>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {SIGNOS_CHEQUEO.map(([k, l]) => (
                    <Campo key={k} label={`${l}:`}><input className={fieldCls} placeholder={l} value={form.campos[k]} onChange={(e) => setForm({ ...form, campos: { ...form.campos, [k]: e.target.value } })} /></Campo>
                  ))}
                  <Campo label="IMC:"><input readOnly tabIndex={-1} className={`${fieldCls} bg-muted/50`} value={imcDe(form.campos)} placeholder="—" /></Campo>
                </div>
                {CHEQUEO_CAMPOS.map(([k, l]) => (
                  <div key={k}>
                    <span className={lbl}>{l}</span>
                    <AutoTextarea className="mt-1" value={form.campos[k]} onChange={(e) => setForm({ ...form, campos: { ...form.campos, [k]: e.target.value } })} />
                  </div>
                ))}
                <DiagnosticosCie dx={form.dx} setDx={(dx) => setForm((f) => (f ? { ...f, dx } : f))} />
                {CHEQUEO_SECCIONES.map(([sec, tit, campos]) => (
                  <div key={sec} className="rounded-md border p-3">
                    <h4 className="mb-2 text-sm font-semibold">{tit}</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {campos.map(([k, l]) => (
                        <div key={k}>
                          <span className={lbl}>{l}</span>
                          <AutoTextarea className="mt-1" value={form.campos[k]} onChange={(e) => setForm({ ...form, campos: { ...form.campos, [k]: e.target.value } })} />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                <div>
                  <span className={lbl}>RECOMENDACIONES</span>
                  <AutoTextarea className="mt-1" value={form.campos.recomendaciones} onChange={(e) => setForm({ ...form, campos: { ...form.campos, recomendaciones: e.target.value } })} />
                </div>
                {form.id && <ArchivosGuardados examenId={form.id} pacienteId={pacienteId} editable />}
                <Dropzone staged={form.staged} setStaged={(f) => setForm((x) => (x ? { ...x, staged: f(x.staged) } : x))} />
                <div className="flex gap-2 border-t pt-3">
                  <Button size="sm" isLoading={guardar.isPending}
                    onClick={() => guardar.mutate({ id: form.id, datos: { ...form.campos, diagnosticos: form.dx }, staged: form.staged }, { onSuccess: () => setForm(null) })}>Guardar</Button>
                  <Button size="sm" variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
                </div>
              </>) : !sel ? (
                <p className="py-10 text-center text-sm text-primary/80">No existe historial de Chequeos</p>
              ) : (<>
                <p className="font-semibold">Chequeo N° {sel.numero ?? '—'} <span className="font-normal text-muted-foreground">· {fmtFH(sel.fecha)}</span></p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-1 rounded-md border px-3 py-2 text-xs">
                  {SIGNOS_CHEQUEO.map(([k, l]) => <p key={k}><b>{l}:</b> {ds(sel.datos, k) || '—'}</p>)}
                  <p><b>IMC:</b> {calcIMC(ds(sel.datos, 'peso'), ds(sel.datos, 'talla')) ?? '—'}</p>
                </div>
                {CHEQUEO_CAMPOS.map(([k, l]) => <CampoVer key={k} label={l} v={ds(sel.datos, k)} />)}
                <DxTabla dx={dxDe(sel)} />
                {CHEQUEO_SECCIONES.map(([sec, tit, campos]) => {
                  const conDatos = campos.some(([k]) => ds(sel.datos, k).trim());
                  if (!conDatos) return null;
                  return (
                    <div key={sec} className="rounded-md border p-3">
                      <h4 className="mb-1 text-sm font-semibold">{tit}</h4>
                      {campos.map(([k, l]) => <CampoVer key={k} label={l} v={ds(sel.datos, k)} />)}
                    </div>
                  );
                })}
                <CampoVer label="RECOMENDACIONES" v={ds(sel.datos, 'recomendaciones')} />
                <ArchivosGuardados examenId={sel.id} pacienteId={pacienteId} editable={false} />
                <div className="flex gap-2 border-t pt-3">
                  <Button size="sm" variant="outline" onClick={() => imprimir(sel)}><Printer className="w-3.5 h-3.5 mr-1" /> Imprimir Informe</Button>
                  <Button size="sm" variant="outline" onClick={() => abrir(sel)}><Pencil className="w-3.5 h-3.5 mr-1" /> Editar</Button>
                  <Button size="sm" variant="outline" className="text-red-500" onClick={async () => { if (!(await dlg.confirmar('¿Eliminar este chequeo?', { peligro: true }))) return; eliminar.mutate(sel.id); }}><Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar</Button>
                  <Button size="sm" variant="outline" onClick={onVolver}>Regresar</Button>
                </div>
              </>)}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 8) Protocolo Operatorio
// ---------------------------------------------------------------------------
const EQUIPO: [string, string][] = [
  ['cirujano', 'Cirujano:'], ['instrumentista', 'Instrumentista:'], ['ayudante1', '1er ayudante:'],
  ['circulante', 'Circulante:'], ['ayudante2', '2do ayudante:'], ['anestesia', 'Anestesia:'],
  ['ayudante3', 'Tercer Ayudante:'], ['ayudante_anestesia', 'Ayudante de Anestesia:'], ['tipo_anestesia', 'Tipo Anestesia:'],
];
const TIEMPOS: [string, string][] = [
  ['dieresis', 'Diéresis:'], ['exposicion', 'Exposición'], ['hallazgos', 'Exploración y hallazgos Quirúrgicos:'],
  ['procedimiento_operatorio', 'Procedimiento Operatorio:'], ['sintesis', 'SÍNTESIS:'],
];

export function ProtocoloOperatorio({ pacienteId, nombre, mision, onVolver }: { pacienteId: string; nombre: string; mision?: MisionEx; onVolver: () => void }) {
  const { data: rows = [], isLoading } = useExamenes(pacienteId, 'protocolo_operatorio');
  const guardar = useGuardarExamen(pacienteId, 'protocolo_operatorio');
  const eliminar = useEliminarExamen(pacienteId);
  const [selId, setSelId] = useState<string | null>(null);
  const [form, setForm] = useState<{ id?: string; campos: Record<string, string>; tipo_operacion: string; histopatologico: string; staged: Staged[] } | null>(null);
  const dlg = useDialogoEnLinea();
  const sel = rows.find((r) => r.id === selId) ?? rows[0] ?? null;

  const CAMPOS_CAB: [string, string][] = [
    ['ciudad', 'Ciudad'], ['dx_preoperatorio', 'Diagnóstico Pre-Operatorio'], ['operacion_proyectada', 'Operación Proyectada:'],
    ['dx_postoperatorio', 'Diagnóstico Post-Operatorio'], ['operacion_realizada', 'Operación Realizada:'],
    ['fecha_cirugia', 'Fecha'], ['hora_inicio', 'Hora de Inicio:'], ['hora_fin', 'Hora de Terminación:'],
  ];
  const TODOS = [...CAMPOS_CAB, ...EQUIPO, ...TIEMPOS, ['complicaciones', 'COMPLICACIONES'] as [string, string], ['dx_histopatologico', 'DX HISTOPATOLÓGICO'] as [string, string]];
  const abrir = (r?: ExamenRow) => setForm({
    id: r?.id,
    campos: Object.fromEntries(TODOS.map(([k]) => [k, r ? ds(r.datos, k) : ''])),
    tipo_operacion: r ? (ds(r.datos, 'tipo_operacion') || 'Electiva') : 'Electiva',
    histopatologico: r ? (ds(r.datos, 'histopatologico') || 'NO') : 'NO',
    staged: [],
  });

  const imprimir = (r: ExamenRow) => imprimirDoc({
    titulo: 'Protocolo Operatorio',
    sub: `Paciente: <b>${esc(nombre)}</b>${misionInline(mision ?? null)} · Protocolo N° ${esc(r.numero ?? '—')} · ${esc(ds(r.datos, 'ciudad'))} ${esc(fmtF(ds(r.datos, 'fecha_cirugia') || r.fecha))}`,
    cuerpo: `<table><tbody>
        ${filaHtml('Diagnóstico Pre-Operatorio', ds(r.datos, 'dx_preoperatorio'))}
        ${filaHtml('Operación Proyectada', `${ds(r.datos, 'operacion_proyectada')} (${ds(r.datos, 'tipo_operacion')})`)}
        ${filaHtml('Diagnóstico Post-Operatorio', ds(r.datos, 'dx_postoperatorio'))}
        ${filaHtml('Operación Realizada', ds(r.datos, 'operacion_realizada'))}
        ${filaHtml('Hora de inicio / terminación', `${ds(r.datos, 'hora_inicio')} — ${ds(r.datos, 'hora_fin')}`)}
      </tbody></table>
      <h2>EQUIPO OPERATORIO</h2><table><tbody>${EQUIPO.map(([k, l]) => filaHtml(l.replace(':', ''), ds(r.datos, k))).join('')}</tbody></table>
      <h2>TIEMPOS QUIRÚRGICOS</h2>${TIEMPOS.map(([k, l]) => campoHtml(l.replace(':', ''), ds(r.datos, k))).join('')}
      ${campoHtml('COMPLICACIONES DEL ACTO OPERATORIO', ds(r.datos, 'complicaciones'))}
      ${ds(r.datos, 'histopatologico') ? `<h3>EXAMEN HISTOPATOLÓGICO: ${esc(ds(r.datos, 'histopatologico'))}</h3>` : ''}
      ${campoHtml('DIAGNÓSTICO HISTOPATOLÓGICO', ds(r.datos, 'dx_histopatologico'))}`,
    firmas: ['FIRMA DEL CIRUJANO', 'FIRMA DEL ANESTESIÓLOGO'],
  });

  if (isLoading) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>;
  return (
    <div className="space-y-3">
      {dlg.ui}
      <CabeceraEx titulo="Documentos — Protocolo Operatorio" onVolver={onVolver} />
      <section>
        <BarraEx titulo="Protocolos Operatorios"
          accion={!form && <Button size="sm" onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nuevo Protocolo</Button>} />
        <div className="rounded-b-md border border-t-0 bg-card p-4">
          <div className="flex gap-4 items-start">
            <Historial rows={rows} selId={sel?.id ?? null} onSel={(id) => { setSelId(id); setForm(null); }} etiqueta={(r) => `Protocolo ${r.numero ?? ''} · ${fmtF(r.fecha)}`} />
            <div className="flex-1 min-w-0 space-y-3 text-sm">
              {form ? (<>
                <PlantillasDoc categoria="protocolo_operatorio" getDatos={() => ({ ...form.campos, tipo_operacion: form.tipo_operacion, histopatologico: form.histopatologico })}
                  onAplicar={(c) => setForm({
                    ...form,
                    campos: Object.fromEntries(TODOS.map(([k]) => [k, typeof c[k] === 'string' ? (c[k] as string) : form.campos[k]])),
                    tipo_operacion: typeof c.tipo_operacion === 'string' ? (c.tipo_operacion as string) : form.tipo_operacion,
                  })} />
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Campo label="Ciudad"><input className={fieldCls} value={form.campos.ciudad} onChange={(e) => setForm({ ...form, campos: { ...form.campos, ciudad: e.target.value } })} /></Campo>
                  <Campo label="Fecha"><input type="date" className={fieldCls} value={form.campos.fecha_cirugia} onChange={(e) => setForm({ ...form, campos: { ...form.campos, fecha_cirugia: e.target.value } })} /></Campo>
                  <Campo label="Hora de Inicio:"><input type="time" className={fieldCls} value={form.campos.hora_inicio} onChange={(e) => setForm({ ...form, campos: { ...form.campos, hora_inicio: e.target.value } })} /></Campo>
                  <Campo label="Hora de Terminación:"><input type="time" className={fieldCls} value={form.campos.hora_fin} onChange={(e) => setForm({ ...form, campos: { ...form.campos, hora_fin: e.target.value } })} /></Campo>
                </div>
                <div><span className={lbl}>Diagnóstico Pre-Operatorio</span><AutoTextarea className="mt-1" value={form.campos.dx_preoperatorio} onChange={(e) => setForm({ ...form, campos: { ...form.campos, dx_preoperatorio: e.target.value } })} /></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
                  <Campo label="Operación Proyectada:"><input className={fieldCls} value={form.campos.operacion_proyectada} onChange={(e) => setForm({ ...form, campos: { ...form.campos, operacion_proyectada: e.target.value } })} /></Campo>
                  <div className="flex items-center gap-4 pb-2">
                    {['Electiva', 'Emergencia', 'Paliativa'].map((t) => (
                      <label key={t} className="flex items-center gap-1.5 cursor-pointer">
                        <input type="radio" className="accent-primary" name="po-tipo" checked={form.tipo_operacion === t} onChange={() => setForm({ ...form, tipo_operacion: t })} /> {t}
                      </label>
                    ))}
                  </div>
                </div>
                <div><span className={lbl}>Diagnóstico Post-Operatorio</span><AutoTextarea className="mt-1" value={form.campos.dx_postoperatorio} onChange={(e) => setForm({ ...form, campos: { ...form.campos, dx_postoperatorio: e.target.value } })} /></div>
                <Campo label="Operación Realizada:"><input className={fieldCls} value={form.campos.operacion_realizada} onChange={(e) => setForm({ ...form, campos: { ...form.campos, operacion_realizada: e.target.value } })} /></Campo>
                <h4 className="text-sm font-semibold border-t pt-3">EQUIPO OPERATORIO</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {EQUIPO.map(([k, l]) => (
                    <Campo key={k} label={l}><input className={fieldCls} value={form.campos[k]} onChange={(e) => setForm({ ...form, campos: { ...form.campos, [k]: e.target.value } })} /></Campo>
                  ))}
                </div>
                <h4 className="text-sm font-semibold border-t pt-3">TIEMPOS QUIRÚRGICOS</h4>
                {TIEMPOS.map(([k, l]) => (
                  <div key={k}>
                    <span className={lbl}>{l}</span>
                    <AutoTextarea className="mt-1" value={form.campos[k]} onChange={(e) => setForm({ ...form, campos: { ...form.campos, [k]: e.target.value } })} />
                  </div>
                ))}
                <div><span className={lbl}>COMPLICACIONES DEL ACTO OPERATORIO:</span><AutoTextarea className="mt-1" value={form.campos.complicaciones} onChange={(e) => setForm({ ...form, campos: { ...form.campos, complicaciones: e.target.value } })} /></div>
                <div className="flex items-center gap-4">
                  <span className={lbl}>EXAMEN HISTOPATOLÓGICO:</span>
                  {['SI', 'NO'].map((t) => (
                    <label key={t} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" className="accent-primary" name="po-histo" checked={form.histopatologico === t} onChange={() => setForm({ ...form, histopatologico: t })} /> {t}
                    </label>
                  ))}
                </div>
                {form.histopatologico === 'SI' && (
                  <div><span className={lbl}>DIAGNÓSTICO HISTOPATOLÓGICO:</span><AutoTextarea className="mt-1" value={form.campos.dx_histopatologico} onChange={(e) => setForm({ ...form, campos: { ...form.campos, dx_histopatologico: e.target.value } })} /></div>
                )}
                {form.id && <ArchivosGuardados examenId={form.id} pacienteId={pacienteId} editable />}
                <Dropzone staged={form.staged} setStaged={(f) => setForm((x) => (x ? { ...x, staged: f(x.staged) } : x))} />
                <div className="flex gap-2 border-t pt-3">
                  <Button size="sm" isLoading={guardar.isPending}
                    onClick={() => guardar.mutate({ id: form.id, datos: { ...form.campos, tipo_operacion: form.tipo_operacion, histopatologico: form.histopatologico }, staged: form.staged }, { onSuccess: () => setForm(null) })}>Guardar</Button>
                  <Button size="sm" variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
                </div>
              </>) : !sel ? (
                <p className="py-10 text-center text-sm text-primary/80">No existe historial de Protocolos Operatorios</p>
              ) : (<>
                <p className="font-semibold">Protocolo N° {sel.numero ?? '—'} <span className="font-normal text-muted-foreground">· {ds(sel.datos, 'ciudad')} {fmtF(ds(sel.datos, 'fecha_cirugia') || sel.fecha)} · {ds(sel.datos, 'hora_inicio')}—{ds(sel.datos, 'hora_fin')}</span></p>
                <CampoVer label="Diagnóstico Pre-Operatorio" v={ds(sel.datos, 'dx_preoperatorio')} />
                <p><b>Operación Proyectada:</b> {ds(sel.datos, 'operacion_proyectada') || '—'} <span className="text-muted-foreground">({ds(sel.datos, 'tipo_operacion')})</span></p>
                <CampoVer label="Diagnóstico Post-Operatorio" v={ds(sel.datos, 'dx_postoperatorio')} />
                <p><b>Operación Realizada:</b> {ds(sel.datos, 'operacion_realizada') || '—'}</p>
                <div className="rounded-md border p-3">
                  <h4 className="mb-1 text-sm font-semibold">EQUIPO OPERATORIO</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1 text-xs">
                    {EQUIPO.map(([k, l]) => <p key={k}><b>{l}</b> {ds(sel.datos, k) || '—'}</p>)}
                  </div>
                </div>
                <h4 className="text-sm font-semibold">TIEMPOS QUIRÚRGICOS</h4>
                {TIEMPOS.map(([k, l]) => <CampoVer key={k} label={l} v={ds(sel.datos, k)} />)}
                <CampoVer label="COMPLICACIONES DEL ACTO OPERATORIO" v={ds(sel.datos, 'complicaciones')} />
                <p><b>Examen histopatológico:</b> {ds(sel.datos, 'histopatologico') || 'NO'}</p>
                <CampoVer label="DIAGNÓSTICO HISTOPATOLÓGICO" v={ds(sel.datos, 'dx_histopatologico')} />
                <ArchivosGuardados examenId={sel.id} pacienteId={pacienteId} editable={false} />
                <div className="flex gap-2 border-t pt-3">
                  <Button size="sm" variant="outline" onClick={() => imprimir(sel)}><Printer className="w-3.5 h-3.5 mr-1" /> Imprimir</Button>
                  <Button size="sm" variant="outline" onClick={() => abrir(sel)}><Pencil className="w-3.5 h-3.5 mr-1" /> Editar</Button>
                  <Button size="sm" variant="outline" className="text-red-500" onClick={async () => { if (!(await dlg.confirmar('¿Eliminar este protocolo?', { peligro: true }))) return; eliminar.mutate(sel.id); }}><Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar</Button>
                  <Button size="sm" variant="outline" onClick={onVolver}>Regresar</Button>
                </div>
              </>)}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 9) Procedimientos — historial con catálogo configurable (pantalla reliv)
// ---------------------------------------------------------------------------
export function ProcedimientosFicha({ pacienteId, nombre, mision, onVolver }: { pacienteId: string; nombre: string; mision?: MisionEx; onVolver: () => void }) {
  const qc = useQueryClient();
  const { data: rows = [], isLoading } = useExamenes(pacienteId, 'procedimiento');
  const guardar = useGuardarExamen(pacienteId, 'procedimiento');
  const eliminar = useEliminarExamen(pacienteId);
  const { data: catalogo = [] } = useQuery<Plantilla[]>({
    queryKey: ['med-plantillas-doc', 'procedimiento_tipo'],
    queryFn: () => med.list<Plantilla>('plantilla', '?select=id,nombre,contenido&deleted_at=is.null&es_activo=is.true&categoria=eq.procedimiento_tipo&order=nombre'),
  });
  const [form, setForm] = useState<{ id?: string; procedimiento: string; descripcion: string; dx: Diagnostico[] } | null>(null);
  const dlg = useDialogoEnLinea();

  const nuevoTipo = useMutation({
    mutationFn: (nombreTipo: string) =>
      medRpc<{ nombre?: string }>('med_plantilla_guardar', { _data: { nombre: nombreTipo.trim(), categoria: 'procedimiento_tipo', contenido: {} } })
        .then(() => nombreTipo.trim()),
    onSuccess: (nombreTipo) => {
      toast.success('Procedimiento agregado al catálogo');
      void qc.invalidateQueries({ queryKey: ['med-plantillas-doc', 'procedimiento_tipo'] });
      if (typeof nombreTipo === 'string') setForm((f) => (f ? { ...f, procedimiento: nombreTipo } : f));
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const pedirNuevoTipo = async () => {
    const v = await dlg.pedir('Nuevo procedimiento', [{ key: 'nombre', label: 'Nombre del nuevo procedimiento (catálogo)' }]);
    if (v === null) return;
    nuevoTipo.mutate(v.nombre);
  };

  const imprimir = () => imprimirDoc({
    titulo: 'Historial de Procedimientos',
    sub: `Paciente: <b>${esc(nombre)}</b>${misionInline(mision ?? null)} · Total: ${rows.length} · Emitido: ${esc(new Date().toLocaleString(LOCALE))}`,
    cuerpo: `<table><thead><tr><th>Fecha</th><th>Procedimiento</th><th>Descripción</th><th>Diagnóstico</th></tr></thead><tbody>
      ${rows.map((r) => `<tr><td>${esc(fmtFH(r.fecha))}</td><td>${esc(ds(r.datos, 'procedimiento'))}</td><td>${esc(ds(r.datos, 'descripcion'))}</td><td>${esc(dxDe(r).map((x) => `${x.cie10_codigo ?? ''} ${x.descripcion}`.trim()).join(' · '))}</td></tr>`).join('')
      || '<tr><td colspan="4" class="c">Sin procedimientos registrados</td></tr>'}
    </tbody></table>`,
  });

  if (isLoading) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>;
  return (
    <div className="space-y-3">
      {dlg.ui}
      <CabeceraEx titulo="Procedimientos" onVolver={onVolver} />
      <section>
        <BarraEx titulo="Historial de Procedimientos"
          accion={!form && (
            <span className="flex gap-2">
              <Button size="sm" variant="outline" onClick={imprimir}><Printer className="w-4 h-4 mr-1" /> Imprimir</Button>
              <Button size="sm" onClick={() => setForm({ procedimiento: '', descripcion: '', dx: [] })}><Plus className="w-4 h-4 mr-1" /> Nuevo</Button>
            </span>
          )} />
        <div className="rounded-b-md border border-t-0 bg-card p-4 space-y-3">
          {form && (
            <div className="space-y-3 rounded-md border p-3 text-sm">
              <p className="font-semibold">Detalle Procedimiento</p>
              <div className="flex flex-wrap items-end gap-2">
                <Campo label="Procedimiento" className="min-w-[260px]">
                  <select className={fieldCls} value={form.procedimiento} onChange={(e) => setForm({ ...form, procedimiento: e.target.value })}>
                    <option value="">Seleccione una opción</option>
                    {catalogo.map((c) => <option key={c.id} value={c.nombre}>{c.nombre}</option>)}
                  </select>
                </Campo>
                <Button size="sm" variant="outline" isLoading={nuevoTipo.isPending} onClick={() => void pedirNuevoTipo()}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Nuevo Procedimiento
                </Button>
              </div>
              <span className={lbl}>Diagnóstico:</span>
              <DiagnosticosCie dx={form.dx} setDx={(dx) => setForm((f) => (f ? { ...f, dx } : f))} />
              <div>
                <span className={lbl}>Descripción del Procedimiento:</span>
                <AutoTextarea className="mt-1" value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
              </div>
              <div className="flex gap-2 border-t pt-3">
                <Button size="sm" variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
                <Button size="sm" isLoading={guardar.isPending} disabled={!form.procedimiento}
                  onClick={() => guardar.mutate({ id: form.id, datos: { procedimiento: form.procedimiento, descripcion: form.descripcion, diagnosticos: form.dx } as Record<string, unknown>, staged: [] }, { onSuccess: () => setForm(null) })}>Guardar</Button>
              </div>
            </div>
          )}

          {rows.length === 0 && !form ? (
            <p className="py-6 text-center text-sm text-primary/80">No existen Procedimientos registrados</p>
          ) : rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b bg-muted text-xs text-muted-foreground">
                  <th className="py-2 px-2 text-left">Fecha</th><th className="py-2 px-2 text-left">Procedimiento</th>
                  <th className="py-2 px-2 text-left">Descripción del Procedimiento</th><th className="py-2 px-2 text-left">Diagnóstico</th>
                  <th className="py-2 px-2 text-right">Acción</th>
                </tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="py-2 px-2 whitespace-nowrap">{fmtFH(r.fecha)}</td>
                      <td className="py-2 px-2 font-medium">{ds(r.datos, 'procedimiento') || '—'}</td>
                      <td className="py-2 px-2 whitespace-pre-wrap">{ds(r.datos, 'descripcion') || '—'}</td>
                      <td className="py-2 px-2">{dxDe(r).map((x) => `${x.cie10_codigo ?? ''} ${x.descripcion}`.trim()).join(' · ') || '—'}</td>
                      <td className="py-2 px-2 text-right whitespace-nowrap">
                        <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" aria-label="Editar"
                          onClick={() => setForm({ id: r.id, procedimiento: ds(r.datos, 'procedimiento'), descripcion: ds(r.datos, 'descripcion'), dx: dxDe(r) })}><Pencil className="w-4 h-4" /></button>
                        <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar" aria-label="Eliminar"
                          onClick={async () => { if (!(await dlg.confirmar('¿Eliminar este procedimiento?', { peligro: true }))) return; eliminar.mutate(r.id); }}><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 10) Recetas — formulario estilo reliv (ficha 360°)
// ---------------------------------------------------------------------------
interface RecRow { id: string; codigo: string | null; fecha: string; estado: string; diagnostico: string | null; medico: string; items: number }
interface RecDet { receta: Record<string, unknown>; items: (ItemReliv & { dosis?: string | null })[] }

export function RecetasFicha({ pacienteId, onVolver }: { pacienteId: string; onVolver: () => void }) {
  const qc = useQueryClient();
  const { data: rows = [], isLoading } = useQuery<RecRow[]>({
    queryKey: ['med-ficha', pacienteId, 'recetas'],
    queryFn: () => medRpc<RecRow[]>('med_recetas', { _paciente_id: pacienteId }),
  });

  const [selId, setSelId] = useState<string | null>(null);
  const [form, setForm] = useState(false);
  const [imprimirId, setImprimirId] = useState<string | null>(null);
  const dlg = useDialogoEnLinea();
  const sel = rows.find((r) => r.id === selId) ?? rows[0] ?? null;

  const { data: det } = useQuery<RecDet>({
    queryKey: ['med-ficha', pacienteId, 'receta-det', sel?.id],
    queryFn: () => medRpc<RecDet>('med_receta', { _id: sel!.id }),
    enabled: !!sel && !form,
  });

  const anular = useMutation({
    mutationFn: (id: string) => medRpc('med_anular_receta', { _id: id, _motivo: 'Anulada por el médico' }),
    onSuccess: () => { toast.success('Receta anulada'); void qc.invalidateQueries({ queryKey: ['med-ficha', pacienteId] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (isLoading) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>;
  const detDatos = (det?.receta?.datos ?? {}) as Record<string, unknown>;
  return (
    <div className="space-y-3">
      {dlg.ui}
      <CabeceraEx titulo="Recetas" onVolver={onVolver} />
      <section>
        <BarraEx titulo="Recetas"
          accion={!form && <Button size="sm" onClick={() => setForm(true)}><Plus className="w-4 h-4 mr-1" /> Nueva receta</Button>} />
        <div className="rounded-b-md border border-t-0 bg-card p-4">
          <div className="flex gap-4 items-start">
            <Historial rows={rows.map((r) => ({ ...r, categoria: 'receta', numero: null, datos: {}, created_at: r.fecha, n_archivos: 0 })) as unknown as ExamenRow[]}
              selId={sel?.id ?? null} onSel={(id) => { setSelId(id); setForm(false); }}
              etiqueta={(r) => `${(r as unknown as RecRow).codigo ?? ''} · ${fmtF(r.fecha)}`} />
            <div className="flex-1 min-w-0 space-y-3 text-sm">
              {form ? (
                <RecetaEditor pacienteId={pacienteId} onCancel={() => setForm(false)}
                  onSaved={() => { setForm(false); void qc.invalidateQueries({ queryKey: ['med-ficha', pacienteId] }); }} />
              ) : !sel ? (
                <p className="py-10 text-center text-sm text-primary/80">No existen recetas — usa «Nueva receta»</p>
              ) : (<>
                <p className="font-semibold">Receta # {sel.codigo} <span className="font-normal text-muted-foreground">· {fmtFH(sel.fecha)} · {sel.medico} {sel.estado === 'anulada' && <span className="text-red-500">· ANULADA</span>}</span></p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 rounded-md border px-3 py-2 text-xs">
                  <p><b>Ciudad:</b> {typeof detDatos.ciudad === 'string' && detDatos.ciudad ? detDatos.ciudad : '—'}</p>
                  <p><b>Próxima cita:</b> {typeof detDatos.proxima_cita === 'string' && detDatos.proxima_cita ? fmtF(detDatos.proxima_cita) : '—'}</p>
                  <p className="col-span-2"><b>Alergias farmacológicas:</b> {typeof detDatos.alergias_farmacologicas === 'string' && detDatos.alergias_farmacologicas ? detDatos.alergias_farmacologicas : '—'}</p>
                </div>
                {Array.isArray(detDatos.signos_alarma) && (detDatos.signos_alarma as string[]).length > 0 && (
                  <p className="text-xs"><b>Signos de alarma:</b> {(detDatos.signos_alarma as string[]).join(' · ')}</p>
                )}
                <CampoVer label="Diagnóstico" v={sel.diagnostico} />
                <DxTabla dx={Array.isArray(detDatos.diagnosticos) ? (detDatos.diagnosticos as Diagnostico[]) : []} />
                {det && det.items.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b bg-muted text-xs text-muted-foreground">
                        <th className="py-1.5 px-2 text-left">Genérico</th><th className="py-1.5 px-2 text-left">Comercial</th>
                        <th className="py-1.5 px-2 text-left">Concentración</th><th className="py-1.5 px-2 text-left">Vía</th>
                        <th className="py-1.5 px-2 text-left">Presentación</th><th className="py-1.5 px-2 text-left">Cantidad</th>
                        <th className="py-1.5 px-2 text-left">Indicaciones</th>
                      </tr></thead>
                      <tbody>
                        {det.items.map((it, i) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="py-1.5 px-2 font-medium">{it.medicamento}</td>
                            <td className="py-1.5 px-2">{it.comercial || '—'}</td>
                            <td className="py-1.5 px-2">{it.concentracion || '—'}</td>
                            <td className="py-1.5 px-2">{it.via || '—'}</td>
                            <td className="py-1.5 px-2">{it.forma || '—'}</td>
                            <td className="py-1.5 px-2">{it.cantidad || '—'}</td>
                            <td className="py-1.5 px-2 whitespace-pre-wrap">{it.indicaciones || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <CampoVer label="Recomendaciones no farmacológicas" v={typeof detDatos.recomendaciones === 'string' ? (detDatos.recomendaciones as string) : null} />
                <div className="flex gap-2 border-t pt-3">
                  <Button size="sm" variant="outline" onClick={() => setImprimirId(sel.id)}><Printer className="w-3.5 h-3.5 mr-1" /> Imprimir</Button>
                  {sel.estado !== 'anulada' && (
                    <Button size="sm" variant="outline" className="text-red-500" onClick={async () => { if (!(await dlg.confirmar(`¿Anular la receta ${sel.codigo}?`, { peligro: true }))) return; anular.mutate(sel.id); }}>Anular</Button>
                  )}
                  <Button size="sm" variant="outline" onClick={onVolver}>Regresar</Button>
                </div>
              </>)}
            </div>
          </div>
        </div>
      </section>

      {imprimirId && <RecetaPrint id={imprimirId} onClose={() => setImprimirId(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 11) Pedidos — pedido genérico estilo reliv (candado 24h, resultados, correo)
// ---------------------------------------------------------------------------
const TIPOS_PEDIDO = ['Laboratorio', 'Patología', 'Imagen', 'Otros'];

// Impresión del pedido AL ESTILO RECETA (699): encabezado con logo de la
// empresa + datos del paciente (CI/edad), SIN centro de costo, y firma del
// médico manual (rúbrica) o electrónica (QR de verificación pública /px/<cod>).
interface PedidoImpresion {
  id: string; numero: number | null; fecha: string; datos: Record<string, unknown>;
  codigo_verificacion: string; empresa: string;
  paciente: string; paciente_ident: string | null;
  paciente_edad: number | null; paciente_edad_meses: number | null;
  medico: string | null; medico_licencia: string | null;
  medico_firma: string | null; medico_firma_modo: string | null; especialidad: string | null;
}

function PedidoPrint({ id, onClose }: { id: string; onClose: () => void }) {
  const { data, isFetching } = useQuery<PedidoImpresion>({
    queryKey: ['med-pedido-impresion', id],
    queryFn: () => medRpc<PedidoImpresion>('med_pedido_impresion', { _id: id }),
  });
  const { user } = useAuth();
  const logo = user?.empresa?.logo;
  // Firma en el impreso: default por médico (firma_modo), con override al imprimir.
  const [firmaModo, setFirmaModo] = useState<'electronica' | 'manual' | null>(null);
  const [media, setMedia] = useState(false);
  const modo = firmaModo ?? (data?.medico_firma_modo === 'manual' ? 'manual' : 'electronica');
  const firmaManual = modo === 'manual';

  const datos = (data?.datos ?? {}) as Record<string, unknown>;
  const ds2 = (k: string) => String(datos[k] ?? '');
  const items = Array.isArray(datos.items) ? (datos.items as ItemPedido[]) : [];
  const dx = Array.isArray(datos.diagnosticos) ? (datos.diagnosticos as Diagnostico[]) : [];
  const ciudad = ds2('ciudad').trim().toUpperCase();
  const fecha = data ? new Date(data.fecha).toLocaleDateString(LOCALE) : '';
  const codNo = (data?.codigo_verificacion ?? '').slice(0, 7).toUpperCase();
  const edadStr = data?.paciente_edad != null
    ? `${data.paciente_edad} años${data.paciente_edad_meses ? ` ${data.paciente_edad_meses} meses` : ''}`
    : '—';
  const urlVerificacion = data ? `${window.location.origin}/px/${data.codigo_verificacion}` : '';

  return (
    <Modal title="Pedido médico" onClose={onClose} wide>
      <style>{`@media print { @page { size: A4 portrait; margin: 10mm; } body * { visibility: hidden !important; } #pedido-print, #pedido-print * { visibility: visible !important; } #pedido-print { position: absolute; left: 0; top: 0; width: 100%; padding: 0; } .no-print { display: none !important; } ${media ? '#pedido-print { max-height: 128mm; overflow: hidden; font-size: 90%; }' : ''} }`}</style>
      {isFetching || !data ? <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div> : (
        <>
          <div id="pedido-print" className="bg-white p-2 text-black">
            {/* Encabezado — mismo patrón de la receta (sin centro de costo) */}
            <div className="flex items-center justify-between">
              <p className="text-[13px] font-semibold">{data.empresa}</p>
              {logo && <img src={logo} alt="" className="h-9 max-w-[120px] object-contain" />}
            </div>
            <hr className="my-1 border-black" />
            <div className="flex justify-between text-[11px]">
              <p><b>Fecha:</b> {ciudad ? `${ciudad}, ` : ''}{fecha}</p>
              <p><b>Pedido #</b> {data.numero ?? '—'} · <b>Cod#</b> {codNo}</p>
            </div>
            <p className="text-[11px]"><b>Paciente:</b> {(data.paciente || '').toUpperCase()}</p>
            <div className="flex justify-between text-[11px]">
              <p><b>CI:</b> {data.paciente_ident ?? 's/i'}</p>
              <p><b>Edad:</b> {edadStr}</p>
            </div>
            <hr className="my-1 border-black" />

            <div className="mb-1.5 text-[11px]"><p className="font-bold">TIPO DE PEDIDO:</p><p>{ds2('tipo_pedido') || '—'}</p></div>
            {(ds2('diagnostico').trim() || dx.length > 0) && (
              <div className="mb-1.5 text-[11px]">
                <p className="font-bold">DIAGNÓSTICO:</p>
                {ds2('diagnostico').trim() && <p className="whitespace-pre-wrap">{ds2('diagnostico')}</p>}
                {dx.map((d, i) => <p key={i}>{`${d.cie10_codigo ?? ''} ${d.descripcion ?? ''}`.trim()}</p>)}
              </div>
            )}
            {items.length > 0 && (
              <div className="mb-1.5 text-[11px]">
                <p className="font-bold">DETALLE DEL PEDIDO:</p>
                <table className="mt-0.5 w-full border-collapse text-[11px]">
                  <thead><tr>
                    <th className="w-20 border border-black px-1.5 py-0.5 text-left">Cantidad</th>
                    <th className="border border-black px-1.5 py-0.5 text-left">Nombre</th>
                  </tr></thead>
                  <tbody>
                    {items.map((it, i) => (
                      <tr key={i}>
                        <td className="border border-black px-1.5 py-0.5 text-center">{it.cantidad || '1'}</td>
                        <td className="border border-black px-1.5 py-0.5">{it.nombre}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {ds2('detalle_otros').trim() && <div className="mb-1.5 text-[11px]"><p className="font-bold">DETALLE TIPO OTROS:</p><p className="whitespace-pre-wrap">{ds2('detalle_otros')}</p></div>}
            {ds2('observaciones').trim() && <div className="mb-1.5 text-[11px]"><p className="font-bold">OBSERVACIONES:</p><p className="whitespace-pre-wrap">{ds2('observaciones')}</p></div>}

            {/* Firma — mismo patrón de la receta: manual = rúbrica · electrónica = QR /px */}
            <div className="mt-6 text-center text-[10px]">
              {firmaManual ? (
                data.medico_firma
                  ? <img src={data.medico_firma} alt="rúbrica del médico" className="mx-auto h-9 object-contain" />
                  : <div className="h-9" />
              ) : (
                <div className="mx-auto flex items-center justify-center gap-2">
                  <QRCodeSVG value={urlVerificacion} size={52} level="M" bgColor="#ffffff" fgColor="#000000" />
                  <div className="text-left text-[8px] leading-tight">
                    <p className="font-bold text-[9px]">EMITIDO ELECTRÓNICAMENTE</p>
                    <p>{data.medico ?? ''}</p>
                    <p className="text-gray-600">Cód. {data.codigo_verificacion.slice(0, 12)}</p>
                    <p className="text-gray-600">Verificar: {urlVerificacion.replace(/^https?:\/\//, '')}</p>
                  </div>
                </div>
              )}
              <div className="mx-auto mt-1 max-w-[70%] border-t px-4 pt-1">
                {data.medico ?? 'FIRMA Y SELLO DEL MÉDICO'}{data.medico_licencia ? ` · Lic. ${data.medico_licencia}` : ''}
              </div>
              {data.especialidad && <p className="text-gray-500">{data.especialidad}</p>}
            </div>
          </div>

          <div className="no-print flex items-center gap-3 pt-3 border-t mt-3 flex-wrap">
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              Firma:
              <select className={`${fieldCls} !w-auto`} value={modo}
                onChange={(e) => setFirmaModo(e.target.value as 'electronica' | 'manual')}>
                <option value="electronica">Electrónica (QR)</option>
                <option value="manual">Manual (rúbrica)</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input type="checkbox" checked={media} onChange={(e) => setMedia(e.target.checked)} /> Media hoja A4
            </label>
            <div className="ml-auto flex gap-2">
              <Button size="sm" variant="outline" onClick={onClose}>Cerrar</Button>
              <Button size="sm" onClick={() => window.print()}><Printer className="w-3.5 h-3.5 mr-1" /> Imprimir</Button>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
// Tipo de pedido → categoría del Catálogo de Exámenes (para el combo de selección).
const CAT_PEDIDO: Record<string, string> = { Laboratorio: 'laboratorio', Imagen: 'imagenologia', 'Patología': 'patologia' };

interface ItemPedido { cantidad: string; nombre: string; catalogo_id?: string | null }
interface FormPedido {
  id?: string; tipo_pedido: string; ciudad: string; diagnostico: string; dx: Diagnostico[];
  items: ItemPedido[]; item_cantidad: string; item_nombre: string; item_cat_id: string;
  detalle_otros: string; observaciones: string; staged: Staged[];
}

// (mision ya no se imprime en el pedido — 699 — pero se conserva en la firma
// del componente para no tocar al llamador; se ignora aquí)
export function PedidosFicha({ pacienteId, genero, onVolver }: { pacienteId: string; nombre: string; genero?: string | null; mision?: MisionEx; onVolver: () => void }) {
  const { data: rows = [], isLoading } = useExamenes(pacienteId, 'pedido');
  const { data: catalogo = [] } = useQuery<Examen[]>({ queryKey: ['med-examenes'], queryFn: () => medRpc<Examen[]>('med_examenes', { _busqueda: '' }) });
  const guardar = useGuardarExamen(pacienteId, 'pedido');
  const eliminar = useEliminarExamen(pacienteId);
  const [selId, setSelId] = useState<string | null>(null);
  const [form, setForm] = useState<FormPedido | null>(null);
  const dlg = useDialogoEnLinea();
  const [buscaOpen, setBuscaOpen] = useState(false);
  // Posición FIJA del desplegable (para que no lo recorte el overflow-x-auto de la ficha).
  const buscaRef = useRef<HTMLInputElement>(null);
  const [buscaRect, setBuscaRect] = useState<DOMRect | null>(null);
  const posicionarBusca = () => { if (buscaRef.current) setBuscaRect(buscaRef.current.getBoundingClientRect()); };
  useEffect(() => {
    if (!buscaOpen) return;
    const upd = () => posicionarBusca();
    window.addEventListener('scroll', upd, true);
    window.addEventListener('resize', upd);
    return () => { window.removeEventListener('scroll', upd, true); window.removeEventListener('resize', upd); };
  }, [buscaOpen]);
  // Typeahead unificado: filtra el catálogo de la categoría del pedido por lo escrito (sin tildes).
  const opcionesBusqueda = useMemo(() => {
    if (!form) return [];
    const cat = CAT_PEDIDO[form.tipo_pedido];
    if (!cat) return [];
    const words = normBuscar(form.item_nombre || '').split(/\s+/).filter(Boolean);
    return catalogo.filter((x) => x.categoria === cat && x.es_activo
      && compatibleConSexo(x.sexo, genero)   // 691: sin exámenes del otro sexo
      && (!words.length || words.every((w) => normBuscar(`${x.codigo ?? ''} ${x.nombre}`).includes(w)))).slice(0, 40);
  }, [catalogo, form, genero]);
  const agregarItem = () => {
    if (!form || !form.item_nombre.trim()) return;
    setForm({ ...form, items: [...form.items, { cantidad: form.item_cantidad.trim() || '1', nombre: form.item_nombre.trim(), catalogo_id: form.item_cat_id || null }], item_cantidad: '', item_nombre: '', item_cat_id: '' });
    setBuscaOpen(false);
  };
  const sel = rows.find((r) => r.id === selId) ?? rows[0] ?? null;

  const itemsDe = (r: ExamenRow): ItemPedido[] => (Array.isArray(r.datos.items) ? (r.datos.items as ItemPedido[]) : []);

  // Último CIE del paciente para el pedido NUEVO: los diagnósticos de la última consulta
  // (misma regla y mismo cache que la receta); si no hay, los del último pedido con CIE.
  const { data: histParaDx } = useQuery<{ evoluciones?: Array<{ evolucion: { episodio_id?: string | null; fecha: string }; diagnosticos?: Diagnostico[] }> }>({
    queryKey: ['med-ficha', pacienteId, 'historia'],
    queryFn: () => medRpc('med_historia', { _paciente_id: pacienteId }),
    enabled: !!pacienteId,
  });
  const ultimoCie = useMemo((): { dx: Diagnostico[]; origen: string } | null => {
    const raices = (histParaDx?.evoluciones ?? []).filter((e) => !e.evolucion.episodio_id && e.diagnosticos?.length);
    const ultima = [...raices].sort((a, b) => (a.evolucion.fecha < b.evolucion.fecha ? 1 : -1))[0];
    if (ultima) return { dx: ultima.diagnosticos!, origen: `última consulta (${new Date(ultima.evolucion.fecha).toLocaleDateString(LOCALE)})` };
    const pedido = rows.find((r) => dxDe(r).length);
    return pedido ? { dx: dxDe(pedido), origen: 'último pedido' } : null;
  }, [histParaDx, rows]);
  const [dxPrecargado, setDxPrecargado] = useState<string | null>(null);

  const abrir = (r?: ExamenRow) => { setDxPrecargado(!r && ultimoCie ? ultimoCie.origen : null); setForm({
    id: r?.id, tipo_pedido: r ? (ds(r.datos, 'tipo_pedido') || 'Laboratorio') : 'Laboratorio',
    ciudad: r ? ds(r.datos, 'ciudad') : '', diagnostico: r ? ds(r.datos, 'diagnostico') : '',
    dx: r ? dxDe(r) : (ultimoCie?.dx ?? []), items: r ? itemsDe(r) : [], item_cantidad: '', item_nombre: '', item_cat_id: '',
    detalle_otros: r ? ds(r.datos, 'detalle_otros') : '', observaciones: r ? ds(r.datos, 'observaciones') : '', staged: [],
  }); };
  const datosDe = (f: FormPedido): Record<string, unknown> => ({
    tipo_pedido: f.tipo_pedido, ciudad: f.ciudad, diagnostico: f.diagnostico, diagnosticos: f.dx,
    items: f.items, detalle_otros: f.detalle_otros, observaciones: f.observaciones,
  });

  const enviarCorreo = useMutation({
    mutationFn: (id: string) => medRpc<{ destino: string }>('med_pedido_enviar_correo', { _examen_id: id }),
    onSuccess: (r) => toast.success(`Pedido encolado por correo a ${r.destino} (lo envía el notificador)`),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // La impresión vive en PedidoPrint (modal al estilo receta, script 699):
  // encabezado con logo + datos del paciente, sin centro de costo, firma
  // manual/electrónica con QR de verificación /px/<código>.
  const [printId, setPrintId] = useState<string | null>(null);

  if (isLoading) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>;
  return (
    <div className="space-y-3">
      {dlg.ui}
      <CabeceraEx titulo="Pedidos" onVolver={onVolver} />
      <section>
        <BarraEx titulo="Pedidos"
          accion={!form && <Button size="sm" onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nuevo Pedido</Button>} />
        <div className="rounded-b-md border border-t-0 bg-card p-4 space-y-3">
          <p className="rounded-md bg-primary/10 px-3 py-2 text-xs">
            ℹ️ Estimado Dr/a: le recordamos que los pedidos se podrán modificar únicamente hasta 24 horas posterior a su creación.
          </p>
          <div className="flex gap-4 items-start">
            <Historial rows={rows} selId={sel?.id ?? null} onSel={(id) => { setSelId(id); setForm(null); }}
              etiqueta={(r) => `Pedido ${r.numero ?? ''} · ${fmtF(r.fecha)}`} />
            <div className="flex-1 min-w-0 space-y-3 text-sm">
              {form ? (<>
                <div className="flex items-center justify-between">
                  <p className="font-semibold">{form.id ? 'Editar pedido' : 'Nuevo pedido'}</p>
                  <PlantillasDoc categoria="pedido" getDatos={() => datosDe(form)}
                    onAplicar={(c) => setForm({
                      ...form,
                      tipo_pedido: typeof c.tipo_pedido === 'string' ? (c.tipo_pedido as string) : form.tipo_pedido,
                      ciudad: typeof c.ciudad === 'string' ? (c.ciudad as string) : form.ciudad,
                      diagnostico: typeof c.diagnostico === 'string' ? (c.diagnostico as string) : form.diagnostico,
                      items: Array.isArray(c.items) && c.items.length ? (c.items as ItemPedido[]) : form.items,
                      detalle_otros: typeof c.detalle_otros === 'string' ? (c.detalle_otros as string) : form.detalle_otros,
                      observaciones: typeof c.observaciones === 'string' ? (c.observaciones as string) : form.observaciones,
                    })} />
                </div>
                <div>
                  <span className={lbl}>Tipo de Pedido:</span>
                  <div className="mt-1 flex flex-wrap items-center gap-4">
                    {TIPOS_PEDIDO.map((t) => (
                      <label key={t} className="flex items-center gap-1.5 cursor-pointer">
                        <input type="radio" className="accent-primary" name="ped-tipo" checked={form.tipo_pedido === t} onChange={() => setForm({ ...form, tipo_pedido: t })} /> {t}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Campo label="Ciudad"><input className={fieldCls} placeholder="Ciudad" value={form.ciudad} onChange={(e) => setForm({ ...form, ciudad: e.target.value })} /></Campo>
                </div>
                <span className={lbl}>Diagnóstico:</span>
                {dxPrecargado && !form.id && form.dx.length > 0 && <p className="text-xs text-muted-foreground">CIE precargado de la {dxPrecargado}: revíselo o cámbielo.</p>}
                <DiagnosticosCie dx={form.dx} setDx={(dx) => setForm((f) => (f ? { ...f, dx } : f))} />
                <AutoTextarea placeholder="Diagnóstico (texto libre)…" value={form.diagnostico} onChange={(e) => setForm({ ...form, diagnostico: e.target.value })} />
                <div>
                  <span className={lbl}>Detalles del Pedido:</span>
                  {form.items.length > 0 && (
                    <table className="mt-1 w-full text-sm">
                      <thead><tr className="border-b bg-muted text-xs text-muted-foreground">
                        <th className="py-1.5 px-2 text-left w-24">Cantidad</th><th className="py-1.5 px-2 text-left">Nombre</th><th className="py-1.5 px-2 w-10"></th>
                      </tr></thead>
                      <tbody>
                        {form.items.map((it, i) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="py-1 px-2">{it.cantidad || '1'}</td>
                            <td className="py-1 px-2">{it.nombre}</td>
                            <td className="py-1 px-2 text-right">
                              <button type="button" className="text-red-500" title="Quitar" aria-label="Quitar"
                                onClick={() => setForm({ ...form, items: form.items.filter((_, j) => j !== i) })}><Trash2 className="w-3.5 h-3.5" /></button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  <div className="mt-1 flex items-end gap-2">
                    <div className="w-16 shrink-0">
                      <span className="mb-0.5 block text-[11px] font-medium text-muted-foreground">Cant.</span>
                      <input className={`${fieldCls} text-center`} placeholder="1" value={form.item_cantidad} onChange={(e) => setForm({ ...form, item_cantidad: e.target.value })} />
                    </div>
                    <div className="relative flex-1 min-w-0">
                      <span className="mb-0.5 block text-[11px] font-medium text-muted-foreground">Examen / estudio</span>
                      <input ref={buscaRef} className={fieldCls} autoComplete="off"
                        placeholder={CAT_PEDIDO[form.tipo_pedido] ? `Buscar en ${form.tipo_pedido.toLowerCase()} o escribir a mano…` : 'Nombre del examen/estudio…'}
                        value={form.item_nombre}
                        onChange={(e) => { setForm({ ...form, item_nombre: e.target.value, item_cat_id: '' }); posicionarBusca(); setBuscaOpen(true); }}
                        onFocus={() => { posicionarBusca(); setBuscaOpen(true); }}
                        onBlur={() => window.setTimeout(() => setBuscaOpen(false), 150)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); agregarItem(); } }} />
                      {buscaOpen && buscaRect && opcionesBusqueda.length > 0 && (
                        <div className="max-h-60 overflow-auto rounded-md border bg-popover text-popover-foreground shadow-lg"
                          style={{ position: 'fixed', top: buscaRect.bottom + 2, left: buscaRect.left, width: buscaRect.width, zIndex: 60 }}>
                          {opcionesBusqueda.map((x) => (
                            <button key={x.id} type="button" className="block w-full px-2 py-1.5 text-left text-sm hover:bg-muted"
                              onMouseDown={(e) => { e.preventDefault(); setForm({ ...form, item_nombre: x.nombre, item_cat_id: x.id }); setBuscaOpen(false); }}>
                              {x.codigo && <span className="mr-1 font-mono text-[11px] text-muted-foreground">{x.codigo}</span>}{x.nombre}
                              {x.subcategoria && <span className="ml-1 text-[10px] text-muted-foreground">· {x.subcategoria}</span>}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button size="sm" variant="outline" disabled={!form.item_nombre.trim()} onClick={agregarItem}>Agregar</Button>
                  </div>
                </div>
                {form.tipo_pedido === 'Otros' && (
                  <div><span className={lbl}>Detalle Tipo Otros:</span><AutoTextarea className="mt-1" value={form.detalle_otros} onChange={(e) => setForm({ ...form, detalle_otros: e.target.value })} /></div>
                )}
                <div><span className={lbl}>Observaciones:</span><AutoTextarea className="mt-1" value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} /></div>
                {form.id && (<>
                  <span className={lbl}>Resultados (archivos):</span>
                  <ArchivosGuardados examenId={form.id} pacienteId={pacienteId} editable />
                </>)}
                <Dropzone staged={form.staged} setStaged={(f) => setForm((x) => (x ? { ...x, staged: f(x.staged) } : x))} />
                <div className="flex gap-2 border-t pt-3">
                  <Button size="sm" isLoading={guardar.isPending} disabled={form.items.length === 0 && !form.item_nombre.trim()}
                    onClick={() => {
                      const items = form.item_nombre.trim()
                        ? [...form.items, { cantidad: form.item_cantidad.trim() || '1', nombre: form.item_nombre.trim(), catalogo_id: form.item_cat_id || null }]
                        : form.items;
                      guardar.mutate({ id: form.id, datos: { ...datosDe(form), items }, staged: form.staged }, { onSuccess: () => setForm(null) });
                    }}>Guardar</Button>
                  <Button size="sm" variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
                </div>
              </>) : !sel ? (
                <p className="py-10 text-center text-sm text-primary/80">No existen pedidos realizados</p>
              ) : (<>
                <p className="font-semibold">Pedido N° {sel.numero ?? '—'} <span className="font-normal text-muted-foreground">· {fmtFH(sel.fecha)} · {ds(sel.datos, 'tipo_pedido')}{ds(sel.datos, 'ciudad') && ` · ${ds(sel.datos, 'ciudad')}`}</span></p>
                <CampoVer label="Diagnóstico" v={ds(sel.datos, 'diagnostico')} />
                <DxTabla dx={dxDe(sel)} />
                {itemsDe(sel).length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b bg-muted text-xs text-muted-foreground">
                        <th className="py-1.5 px-2 text-left w-24">Cantidad</th><th className="py-1.5 px-2 text-left">Nombre</th>
                      </tr></thead>
                      <tbody>
                        {itemsDe(sel).map((it, i) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="py-1.5 px-2">{it.cantidad || '1'}</td>
                            <td className="py-1.5 px-2">{it.nombre}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <CampoVer label="Detalle Tipo Otros" v={ds(sel.datos, 'detalle_otros')} />
                <CampoVer label="Observaciones" v={ds(sel.datos, 'observaciones')} />
                <div>
                  <span className={lbl}>Resultados:</span>
                  <div className="mt-1"><ArchivosGuardados examenId={sel.id} pacienteId={pacienteId} editable /></div>
                </div>
                <div className="flex flex-wrap gap-2 border-t pt-3">
                  <Button size="sm" variant="outline" onClick={() => setPrintId(sel.id)}><Printer className="w-3.5 h-3.5 mr-1" /> Imprimir</Button>
                  <Button size="sm" variant="outline" isLoading={enviarCorreo.isPending} onClick={() => enviarCorreo.mutate(sel.id)}>Enviar Correo</Button>
                  {editable24h(sel) && <Button size="sm" variant="outline" onClick={() => abrir(sel)}><Pencil className="w-3.5 h-3.5 mr-1" /> Editar</Button>}
                  {editable24h(sel) && <Button size="sm" variant="outline" className="text-red-500" onClick={async () => { if (!(await dlg.confirmar('¿Eliminar este pedido?', { peligro: true }))) return; eliminar.mutate(sel.id); }}><Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar</Button>}
                  <Button size="sm" variant="outline" onClick={onVolver}>Regresar</Button>
                </div>
              </>)}
            </div>
          </div>
        </div>
      </section>
      {printId && <PedidoPrint id={printId} onClose={() => setPrintId(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 12) Cobros/Cargos — cobros y presupuestos del paciente (registro clínico-
//     administrativo SIN efecto financiero; la facturación real va aparte)
// ---------------------------------------------------------------------------
interface ItemCobro {
  tipo: 'producto' | 'servicio'; producto_id?: string | null; cantidad: number; descripcion: string;
  precio: number; iva_pct: number; desc_pct: number; desc_valor: number;
}
interface DescGeneral { activo: boolean; tipo: '$' | '%'; valor: number }
interface FormCobro {
  id?: string; seleccion: 'producto' | 'servicio'; observaciones: string; dx: Diagnostico[];
  items: ItemCobro[]; desc: DescGeneral;
}
interface ProdCobro { id: string; codigo: string | null; nombre: string; precio_base: number | null; iva_tarifa: number | null }
interface ExamenCobro { id: string; codigo: string | null; nombre: string; precio: number }
interface MedicoCobro { id: string; razon_social: string; precio_consulta: number }

const n2 = (v: number) => Math.round(v * 100) / 100;
const fmt$ = (v: number) => `$ ${n2(v).toFixed(2)}`;

// Cálculo: por ítem base=cant×precio; desc ítem = $ + %·base; el descuento
// general (% sobre base neta, o $ prorrateado por base) se suma al descuento;
// IVA sobre la base neta final de cada ítem.
function calcularCobro(items: ItemCobro[], desc: DescGeneral) {
  const bases = items.map((it) => n2(it.cantidad * it.precio));
  const netos1 = items.map((it, i) => Math.max(0, bases[i] - it.desc_valor - bases[i] * (it.desc_pct / 100)));
  const sumaNeto1 = netos1.reduce((a, b) => a + b, 0);
  const descGenTotal = !desc.activo || desc.valor <= 0 ? 0
    : desc.tipo === '%' ? sumaNeto1 * (desc.valor / 100) : Math.min(desc.valor, sumaNeto1);
  const filas = items.map((it, i) => {
    const prorrateo = sumaNeto1 > 0 ? descGenTotal * (netos1[i] / sumaNeto1) : 0;
    const neto = Math.max(0, netos1[i] - prorrateo);
    const iva = neto * (it.iva_pct / 100);
    return { base: n2(bases[i]), descuento: n2(bases[i] - neto), iva: n2(iva), total: n2(neto + iva) };
  });
  const subtotal = n2(bases.reduce((a, b) => a + b, 0));
  const descuento = n2(filas.reduce((a, f) => a + f.descuento, 0));
  const iva = n2(filas.reduce((a, f) => a + f.iva, 0));
  return { filas, subtotal, descuento, iva, total: n2(subtotal - descuento + iva) };
}

export function CobrosFicha({ pacienteId, nombre, onVolver }: { pacienteId: string; nombre: string; onVolver: () => void }) {
  const [modo, setModo] = useState<'cobro' | 'presupuesto'>('cobro');
  const { data: cobros = [], isLoading: l1 } = useExamenes(pacienteId, 'cobro');
  const { data: presupuestos = [], isLoading: l2 } = useExamenes(pacienteId, 'presupuesto');
  const guardarCobro = useGuardarExamen(pacienteId, 'cobro');
  const guardarPres = useGuardarExamen(pacienteId, 'presupuesto');
  const eliminar = useEliminarExamen(pacienteId);
  const [selId, setSelId] = useState<string | null>(null);
  const [form, setForm] = useState<FormCobro | null>(null);
  const [modal, setModal] = useState<'producto' | 'servicio' | null>(null);
  const [busq, setBusq] = useState('');
  const dlg = useDialogoEnLinea();

  const rows = modo === 'cobro' ? cobros : presupuestos;
  const sel = rows.find((r) => r.id === selId) ?? rows[0] ?? null;

  const { data: productos = [] } = useQuery<ProdCobro[]>({
    queryKey: ['inv-productos-cobro'],
    queryFn: async () => (await apiClient.get<ProdCobro[]>('/rest/inventario.productos?es_vendible=eq.true&deleted_at=is.null&tiene_variantes=eq.false&es_activo=eq.true&select=id,codigo,nombre,precio_base,iva_tarifa&order=nombre&limit=1000')).data,
    enabled: modal === 'producto',
  });
  const { data: examenes = [] } = useQuery<ExamenCobro[]>({
    queryKey: ['med-examenes-cobro'],
    queryFn: () => medRpc<ExamenCobro[]>('med_examenes', { _busqueda: '' }),
    enabled: modal === 'servicio',
  });
  const { data: medicosC = [] } = useQuery<MedicoCobro[]>({
    queryKey: ['med-medicos-cobro'],
    queryFn: () => medRpc<MedicoCobro[]>('med_medicos', { _busqueda: '' }),
    enabled: modal === 'servicio',
  });

  const itemsDe = (r: ExamenRow): ItemCobro[] => (Array.isArray(r.datos.items) ? (r.datos.items as ItemCobro[]) : []);
  const descDe = (r: ExamenRow): DescGeneral => (r.datos.desc_general && typeof r.datos.desc_general === 'object'
    ? (r.datos.desc_general as DescGeneral) : { activo: false, tipo: '$', valor: 0 });

  const abrir = (r?: ExamenRow) => setForm({
    id: r?.id, seleccion: 'producto', observaciones: r ? ds(r.datos, 'observaciones') : '',
    dx: r ? dxDe(r) : [], items: r ? itemsDe(r) : [], desc: r ? descDe(r) : { activo: false, tipo: '$', valor: 0 },
  });
  const setItem = (i: number, patch: Partial<ItemCobro>) =>
    setForm((f) => (f ? { ...f, items: f.items.map((it, j) => (j === i ? { ...it, ...patch } : it)) } : f));
  const addItem = (it: ItemCobro) => { setForm((f) => (f ? { ...f, items: [...f.items, it] } : f)); setModal(null); };

  const guardar = (como: 'cobro' | 'presupuesto') => {
    if (!form) return;
    const calc = calcularCobro(form.items, form.desc);
    const datos = {
      observaciones: form.observaciones, diagnosticos: form.dx, desc_general: form.desc,
      items: form.items, totales: { subtotal: calc.subtotal, descuento: calc.descuento, iva: calc.iva, total: calc.total },
    };
    const m = como === 'cobro' ? guardarCobro : guardarPres;
    m.mutate({ id: form.id, datos, staged: [] }, { onSuccess: () => { setForm(null); setModo(como); } });
  };

  // Presupuesto → Cobro: crea el cobro con los mismos datos y marca el presupuesto
  const generarCobro = (r: ExamenRow) => {
    guardarCobro.mutate({ datos: { ...r.datos, generado_de_presupuesto: r.numero }, staged: [] }, {
      onSuccess: () => {
        guardarPres.mutate({ id: r.id, datos: { ...r.datos, convertido: true }, staged: [] }, {
          onSuccess: () => { toast.success('Cobro generado desde el presupuesto'); setModo('cobro'); },
        });
      },
    });
  };

  const imprimir = (r: ExamenRow) => {
    const items = itemsDe(r);
    const calc = calcularCobro(items, descDe(r));
    const tabla = (tipo: 'producto' | 'servicio', titulo: string) => {
      const idx = items.map((it, i) => [it, i] as const).filter(([it]) => it.tipo === tipo);
      if (!idx.length) return '';
      return `<h3>${titulo}</h3><table><thead><tr><th>Cantidad</th><th>Descripción</th><th>Precio</th><th>Subtotal</th><th>Desc.</th><th>IVA</th><th>Total ítem</th></tr></thead><tbody>
        ${idx.map(([it, i]) => `<tr><td class="c">${esc(it.cantidad)}</td><td>${esc(it.descripcion)}</td><td class="c">${esc(fmt$(it.precio))}</td><td class="c">${esc(fmt$(calc.filas[i].base))}</td><td class="c">${esc(fmt$(calc.filas[i].descuento))}</td><td class="c">${esc(fmt$(calc.filas[i].iva))}</td><td class="c">${esc(fmt$(calc.filas[i].total))}</td></tr>`).join('')}
      </tbody></table>`;
    };
    imprimirDoc({
      titulo: `${r.categoria === 'presupuesto' ? 'Presupuesto' : 'Cobro'} N° ${r.numero ?? ''}`,
      sub: `Paciente: <b>${esc(nombre)}</b> · ${esc(fmtFH(r.fecha))}`,
      cuerpo: `${campoHtml('Observaciones', ds(r.datos, 'observaciones'))}${dxHtml(dxDe(r))}
        ${tabla('producto', 'Listado Productos')}${tabla('servicio', 'Listado Servicios')}
        <table style="margin-top:8px"><tbody>
          <tr><td class="l">Subtotal</td><td>${esc(fmt$(calc.subtotal))}</td></tr>
          <tr><td class="l">Descuento total</td><td>${esc(fmt$(calc.descuento))}</td></tr>
          <tr><td class="l">IVA</td><td>${esc(fmt$(calc.iva))}</td></tr>
          <tr><td class="l">TOTAL</td><td><b>${esc(fmt$(calc.total))}</b></td></tr>
        </tbody></table>`,
    });
  };

  const renderTablaItems = (tipo: 'producto' | 'servicio', titulo: string, items: ItemCobro[],
    calc: ReturnType<typeof calcularCobro>, editable: boolean) => {
    const idx = items.map((it, i) => [it, i] as const).filter(([it]) => it.tipo === tipo);
    if (!idx.length) return null;
    return (
      <div className="overflow-x-auto">
        <h4 className={lbl}>{titulo}</h4>
        <table className="mt-1 w-full text-xs">
          <thead><tr className="border-b bg-muted text-muted-foreground">
            <th className="py-1.5 px-1">Cantidad</th><th className="py-1.5 px-1 text-left">Descripción</th>
            <th className="py-1.5 px-1">Precio Unitario</th><th className="py-1.5 px-1">Subtotal</th><th className="py-1.5 px-1">IVA %</th>
            <th className="py-1.5 px-1">Descuento (%)</th><th className="py-1.5 px-1">Descuento ($)</th><th className="py-1.5 px-1">Total ítem</th>
            {editable && <th className="py-1.5 px-1"></th>}
          </tr></thead>
          <tbody>
            {idx.map(([it, i]) => (
              <tr key={i} className="border-b last:border-0">
                <td className="py-1 px-1 text-center">
                  {editable ? <input type="number" min={0} step="0.01" className={`${fieldCls} w-16 text-xs text-center`} value={it.cantidad} onChange={(e) => setItem(i, { cantidad: Number(e.target.value) || 0 })} /> : it.cantidad}
                </td>
                <td className="py-1 px-1">{it.descripcion}</td>
                <td className="py-1 px-1 text-center">
                  {editable ? <input type="number" min={0} step="0.01" className={`${fieldCls} w-20 text-xs text-center`} value={it.precio} onChange={(e) => setItem(i, { precio: Number(e.target.value) || 0 })} /> : fmt$(it.precio)}
                </td>
                <td className="py-1 px-1 text-center">{fmt$(calc.filas[i].base)}</td>
                <td className="py-1 px-1 text-center">{it.iva_pct}%</td>
                <td className="py-1 px-1 text-center">
                  {editable ? <input type="number" min={0} max={100} className={`${fieldCls} w-14 text-xs text-center`} value={it.desc_pct} onChange={(e) => setItem(i, { desc_pct: Number(e.target.value) || 0 })} /> : `${it.desc_pct}%`}
                </td>
                <td className="py-1 px-1 text-center">
                  {editable ? <input type="number" min={0} step="0.01" className={`${fieldCls} w-16 text-xs text-center`} value={it.desc_valor} onChange={(e) => setItem(i, { desc_valor: Number(e.target.value) || 0 })} /> : fmt$(it.desc_valor)}
                </td>
                <td className="py-1 px-1 text-center font-medium">{fmt$(calc.filas[i].total)}</td>
                {editable && (
                  <td className="py-1 px-1 text-right">
                    <button type="button" className="text-red-500" title="Quitar" aria-label="Quitar"
                      onClick={() => setForm((f) => (f ? { ...f, items: f.items.filter((_, j) => j !== i) } : f))}><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderTotales = (calc: ReturnType<typeof calcularCobro>) => (
    <div className="ml-auto w-64 space-y-1 rounded-md border px-3 py-2 text-sm">
      <p className="flex justify-between"><span>Subtotal</span><span>{fmt$(calc.subtotal)}</span></p>
      <p className="flex justify-between"><span>Descuento total</span><span>{fmt$(calc.descuento)}</span></p>
      <p className="flex justify-between"><span>IVA</span><span>{fmt$(calc.iva)}</span></p>
      <p className="flex justify-between border-t pt-1 font-semibold"><span>Total</span><span>{fmt$(calc.total)}</span></p>
    </div>
  );

  if (l1 || l2) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>;
  const calcForm = form ? calcularCobro(form.items, form.desc) : null;
  const calcSel = sel ? calcularCobro(itemsDe(sel), descDe(sel)) : null;
  return (
    <div className="space-y-3">
      {dlg.ui}
      <CabeceraEx titulo="Cobros/Cargos" onVolver={onVolver} />
      <section>
        <BarraEx titulo="Cobros"
          accion={!form && <Button size="sm" onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nuevo cobro</Button>} />
        <div className="rounded-b-md border border-t-0 bg-card p-4 space-y-3">
          <div className="flex gap-1 border-b">
            {([['cobro', 'Historial Cobros'], ['presupuesto', 'Historial Presupuesto']] as const).map(([m, l]) => (
              <button key={m} type="button" onClick={() => { setModo(m); setSelId(null); setForm(null); }}
                className={`px-3 py-2 text-sm -mb-px border-b-2 ${modo === m ? 'border-primary text-primary font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
                {l}
              </button>
            ))}
          </div>
          <div className="flex gap-4 items-start">
            <Historial rows={rows} selId={sel?.id ?? null} onSel={(id) => { setSelId(id); setForm(null); }}
              etiqueta={(r) => `${modo === 'cobro' ? 'Cobro' : 'Presup.'} ${r.numero ?? ''} · ${fmtF(r.fecha)}${r.datos.convertido === true ? ' ✔' : ''}`} />
            <div className="flex-1 min-w-0 space-y-3 text-sm">
              {form && calcForm ? (<>
                <div className="flex items-center justify-between">
                  <p className="font-semibold">{form.id ? 'Editar' : 'Nuevo'} cobro / presupuesto</p>
                  <PlantillasDoc categoria="cobro"
                    getDatos={() => ({ observaciones: form.observaciones, items: form.items, desc_general: form.desc })}
                    onAplicar={(c) => setForm({
                      ...form,
                      observaciones: typeof c.observaciones === 'string' ? (c.observaciones as string) : form.observaciones,
                      items: Array.isArray(c.items) && c.items.length ? (c.items as ItemCobro[]) : form.items,
                      desc: c.desc_general && typeof c.desc_general === 'object' ? (c.desc_general as DescGeneral) : form.desc,
                    })} />
                </div>
                <div className="flex flex-wrap items-center gap-4">
                  <span className={lbl}>Seleccione:</span>
                  {(['producto', 'servicio'] as const).map((t) => (
                    <label key={t} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" className="accent-primary" name="cob-sel" checked={form.seleccion === t} onChange={() => setForm({ ...form, seleccion: t })} /> {t === 'producto' ? 'Productos' : 'Servicios'}
                    </label>
                  ))}
                  <Button size="sm" variant="outline" onClick={() => { setBusq(''); setModal(form.seleccion); }}><Plus className="w-3.5 h-3.5 mr-1" /> Agregar</Button>
                </div>
                <div><span className={lbl}>Observaciones:</span><AutoTextarea className="mt-1" value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} /></div>
                <span className={lbl}>Código CIE:</span>
                <DiagnosticosCie dx={form.dx} setDx={(dx) => setForm((f) => (f ? { ...f, dx } : f))} />
                <div className="flex flex-wrap items-center gap-3 rounded-md border px-3 py-2">
                  <label className="flex items-center gap-2 cursor-pointer text-sm">
                    <input type="checkbox" className="accent-primary" checked={form.desc.activo} onChange={(e) => setForm({ ...form, desc: { ...form.desc, activo: e.target.checked } })} /> Aplicar descuento general
                  </label>
                  {form.desc.activo && (<>
                    {(['$', '%'] as const).map((t) => (
                      <label key={t} className="flex items-center gap-1.5 cursor-pointer text-sm">
                        <input type="radio" className="accent-primary" name="cob-desc" checked={form.desc.tipo === t} onChange={() => setForm({ ...form, desc: { ...form.desc, tipo: t } })} /> Descuento ({t})
                      </label>
                    ))}
                    <input type="number" min={0} step="0.01" className={`${fieldCls} w-24`} placeholder="0,00"
                      value={form.desc.valor || ''} onChange={(e) => setForm({ ...form, desc: { ...form.desc, valor: Number(e.target.value) || 0 } })} />
                  </>)}
                </div>
                {renderTablaItems('producto', 'Listado Productos:', form.items, calcForm, true)}
                {renderTablaItems('servicio', 'Listado Servicios:', form.items, calcForm, true)}
                {renderTotales(calcForm)}
                <div className="flex flex-wrap gap-2 border-t pt-3">
                  <Button size="sm" isLoading={guardarCobro.isPending} disabled={!form.items.length} onClick={() => guardar('cobro')}>Guardar Cobro</Button>
                  <Button size="sm" variant="outline" isLoading={guardarPres.isPending} disabled={!form.items.length} onClick={() => guardar('presupuesto')}>Guardar Presupuesto</Button>
                  <Button size="sm" variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
                </div>
              </>) : !sel || !calcSel ? (
                <p className="py-10 text-center text-sm text-primary/80">{modo === 'cobro' ? 'No existen cobros realizados' : 'No existen presupuestos realizados'}</p>
              ) : (<>
                <p className="font-semibold">
                  {modo === 'cobro' ? 'Cobro' : 'Presupuesto'} N° {sel.numero ?? '—'}
                  <span className="font-normal text-muted-foreground"> · {fmtFH(sel.fecha)}</span>
                  {sel.datos.convertido === true && <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-xs text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">CONVERTIDO EN COBRO</span>}
                  {typeof sel.datos.generado_de_presupuesto === 'number' && <span className="ml-2 text-xs text-muted-foreground">(del presupuesto N° {sel.datos.generado_de_presupuesto as number})</span>}
                </p>
                <CampoVer label="Observaciones" v={ds(sel.datos, 'observaciones')} />
                <DxTabla dx={dxDe(sel)} />
                {renderTablaItems('producto', 'Listado Productos:', itemsDe(sel), calcSel, false)}
                {renderTablaItems('servicio', 'Listado Servicios:', itemsDe(sel), calcSel, false)}
                {renderTotales(calcSel)}
                <div className="flex flex-wrap gap-2 border-t pt-3">
                  <Button size="sm" variant="outline" onClick={() => imprimir(sel)}><Printer className="w-3.5 h-3.5 mr-1" /> Imprimir</Button>
                  {modo === 'presupuesto' && sel.datos.convertido !== true && (
                    <Button size="sm" isLoading={guardarCobro.isPending} onClick={() => generarCobro(sel)}>Generar Cobro</Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => abrir(sel)}><Pencil className="w-3.5 h-3.5 mr-1" /> {modo === 'presupuesto' ? 'Editar Presupuesto' : 'Editar'}</Button>
                  <Button size="sm" variant="outline" className="text-red-500" onClick={async () => { if (!(await dlg.confirmar('¿Eliminar este registro?', { peligro: true }))) return; eliminar.mutate(sel.id); }}><Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar</Button>
                  <Button size="sm" variant="outline" onClick={onVolver}>Regresar</Button>
                </div>
              </>)}
            </div>
          </div>
        </div>
      </section>

      {/* Modal: Productos (inventario, con P.V.P e IVA por norma) */}
      {modal === 'producto' && (
        <Modal title="Productos" onClose={() => setModal(null)} wide>
          <div className="space-y-2">
            <input className={fieldCls} placeholder="Buscar…" autoFocus value={busq} onChange={(e) => setBusq(e.target.value)} />
            <div className="max-h-[50vh] overflow-y-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted"><tr className="text-left text-xs text-muted-foreground">
                  <th className="py-2 px-3">Código</th><th className="py-2 px-3">Nombre</th><th className="py-2 px-3">P.V.P</th><th className="py-2 px-3">IVA</th>
                </tr></thead>
                <tbody>
                  {productos.filter((p) => !busq.trim() || p.nombre.toLowerCase().includes(busq.toLowerCase()) || (p.codigo ?? '').toLowerCase().includes(busq.toLowerCase())).slice(0, 100).map((p) => (
                    <tr key={p.id} className="border-t cursor-pointer hover:bg-primary/5"
                      onClick={() => addItem({ tipo: 'producto', producto_id: p.id, cantidad: 1, descripcion: p.nombre, precio: Number(p.precio_base) || 0, iva_pct: Number(p.iva_tarifa) || 0, desc_pct: 0, desc_valor: 0 })}>
                      <td className="py-1.5 px-3 font-mono">{p.codigo ?? '—'}</td>
                      <td className="py-1.5 px-3 font-medium">{p.nombre}</td>
                      <td className="py-1.5 px-3">{fmt$(Number(p.precio_base) || 0)}</td>
                      <td className="py-1.5 px-3">{Number(p.iva_tarifa) || 0}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end"><Button size="sm" variant="outline" onClick={() => setModal(null)}>Cerrar</Button></div>
          </div>
        </Modal>
      )}

      {/* Modal: Servicios (exámenes del catálogo + consulta por médico + línea libre) */}
      {modal === 'servicio' && (
        <Modal title="Servicios" onClose={() => setModal(null)} wide>
          <div className="space-y-2">
            <input className={fieldCls} placeholder="Buscar…" autoFocus value={busq} onChange={(e) => setBusq(e.target.value)} />
            <div className="max-h-[50vh] overflow-y-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted"><tr className="text-left text-xs text-muted-foreground">
                  <th className="py-2 px-3">Código</th><th className="py-2 px-3">Nombre</th><th className="py-2 px-3">Precio Servicio</th>
                </tr></thead>
                <tbody>
                  {medicosC.filter((m) => !busq.trim() || m.razon_social.toLowerCase().includes(busq.toLowerCase())).map((m) => (
                    <tr key={m.id} className="border-t cursor-pointer hover:bg-primary/5"
                      onClick={() => addItem({ tipo: 'servicio', cantidad: 1, descripcion: `Consulta médica — ${m.razon_social}`, precio: Number(m.precio_consulta) || 0, iva_pct: 0, desc_pct: 0, desc_valor: 0 })}>
                      <td className="py-1.5 px-3 font-mono">CONSULTA</td>
                      <td className="py-1.5 px-3 font-medium">Consulta médica — {m.razon_social}</td>
                      <td className="py-1.5 px-3">{fmt$(Number(m.precio_consulta) || 0)}</td>
                    </tr>
                  ))}
                  {examenes.filter((x) => !busq.trim() || x.nombre.toLowerCase().includes(busq.toLowerCase()) || (x.codigo ?? '').toLowerCase().includes(busq.toLowerCase())).slice(0, 100).map((x) => (
                    <tr key={x.id} className="border-t cursor-pointer hover:bg-primary/5"
                      onClick={() => addItem({ tipo: 'servicio', cantidad: 1, descripcion: x.nombre, precio: Number(x.precio) || 0, iva_pct: 0, desc_pct: 0, desc_valor: 0 })}>
                      <td className="py-1.5 px-3 font-mono">{x.codigo ?? '—'}</td>
                      <td className="py-1.5 px-3 font-medium">{x.nombre}</td>
                      <td className="py-1.5 px-3">{fmt$(Number(x.precio) || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <LineaLibreServicio onAdd={(descripcion, precio) => addItem({ tipo: 'servicio', cantidad: 1, descripcion, precio, iva_pct: 0, desc_pct: 0, desc_valor: 0 })} />
            <div className="flex justify-end"><Button size="sm" variant="outline" onClick={() => setModal(null)}>Cerrar</Button></div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function LineaLibreServicio({ onAdd }: { onAdd: (descripcion: string, precio: number) => void }) {
  const [desc, setDesc] = useState('');
  const [precio, setPrecio] = useState('');
  return (
    <div className="flex gap-2 items-center">
      <input className={`${fieldCls} flex-1`} placeholder="Servicio libre (descripción)…" value={desc} onChange={(e) => setDesc(e.target.value)} />
      <input type="number" min={0} step="0.01" className={`${fieldCls} w-28`} placeholder="Precio" value={precio} onChange={(e) => setPrecio(e.target.value)} />
      <Button size="sm" variant="outline" disabled={!desc.trim()} onClick={() => { onAdd(desc.trim(), Number(precio) || 0); setDesc(''); setPrecio(''); }}>Agregar</Button>
    </div>
  );
}
